import axios, { AxiosRequestConfig } from 'axios';
// eslint-disable-next-line import/no-extraneous-dependencies
import qs from 'qs';
import type { Dayjs } from 'dayjs';

import { AxiosServiceResponse, PagedList } from 'interfaces/general';

import { UserItem } from './security';
import { ITicketProblemResponse, SaveTicketRequest } from './ticket';
import { CreatenewTaskTemplate } from './catalog';

const instance = axios.create({
  baseURL: `${process.env.PROCESS_SERVICE_ENDPOINT}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

export interface ItemCABResponse {
  id: number;
  name: string;
  description: string;
  members: UserItem[];
}
export interface IApproveChangeRequest {
  approvalId: number;
  isApproved: boolean;
  comment: string;
}

export interface ISendApprovalNotification {
  changeStepId: number;
  approvalIds: number[];
  emailSubject: string;
  emailBody: string;
}

export interface IAddApprovalMembers {
  changeStepId: number;
  systemUserIds: number[];
}

interface IParams {
  clientId?: number;
  isExport?: boolean;
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  pageSize?: number;
  text?: string;
  enabled?: boolean;
}
interface Types {
  id?: number;
  label?: string;
}
export type TCommentItem = {
  comment: string;
  createdByUserName: string;
  createdDateTime: Date | string;
  id: number;
};

export interface IParamsChange {
  id?: number;
  locationId?: number | string;
  requestedByUserId?: number;
  changeTypeId?: number;
  changeServiceId?: number;
  clientId?: number | string;
  categoryId?: number;
  subcategoryId?: number;
  assignmentPriorityId?: number;
  riskId?: number;
  engineerId?: number;
  ownerSystemUserId?: number;
  isServiceAffected?: boolean;
  changeFlowId?: number;
  description?: string;
  isExport?: boolean;
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  pageSize?: number;
  text?: string;
  search?: string;
  title?: string;
  nameChange?: string;
  textType?: string;
  stateChange?: number;
  changeTypeIds?: Types[];
  typeChange?: Types[];
  stageChange?: Types;
  changeStageIds?: Types[];
  changeStatusIds?: number[];
  changeServiceIds?: Types[];
  categoryIds?: Types;
  subcategoryIds?: Types;
  assignmentPriorityIds?: number[];
  createdDateTimeStart?: string;
  createdDateTimeEnd?: string;
  startDateTimeStart?: string;
  startDateTimeEnd?: string;
  endDateTimeStart?: string;
  endDateTimeEnd?: string;
  startDateTime?: string;
  approvalDateTimeStart?: string;
  approvalDateTimeEnd?: string;
  background?: string;
  downtimeHours?: number;
  dateEnd?: Date;
  dateInitial?: Date;
  createdDateTime?: Date;
  assignedEngineerDateTime?: Date;
  requestedEmail?: string;
  requesterEmail?: string;
  endDateTime?: Date;
  cabMember?: number;
  changeStepId?: number;
  comments?: TCommentItem[];
  urgencyId?: number;
  impactId?: number;
  closureRelevantInfo?: string;
  reviewRelevantInfo?: string;
  cost?: number;
  currencyId?: number;
  unusualSkills?: string;
  specificTechnologies?: string;
  systemGroupId?: number;
  systemGroupName?: string;
  assignedCommitte?: number;
}
export interface ReponseChangeSearch {
  assignmentPriorityId?: number;
  assignmentPriorityName?: string;
  riskId?: number;
  riskName?: string;
  categoryId?: number;
  categoryName?: string;
  changeServiceId?: number;
  changeServiceName?: string;
  changeStageId?: number;
  changeStageName?: string;
  changeStatusId?: number;
  changeStatusName?: string;
  changeTypeId?: number;
  changeTypeName?: string;
  assignedEngineerDateTime?: string;
  description?: string;
  endDateTime?: string;
  id?: number;
  startDateTime?: string;
  subcategoryId?: number;
  subcategoryName?: string;
  title?: string;
  hasPendingApprovals?: boolean;
  approvalId?: number;
  changeStepId?: number;
  comments?: TCommentItem[];
  createdDateTime?: Date | string;
}

export interface IParamsSearchApprovals {
  systemUserId?: number;
  approvalStatusId?: number;
  changeStepId?: number;
  approvalId?: number;
  isExport?: boolean;
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  pageSize?: number;
  text?: string;
}
export interface IChangesApprovalItem {
  changeStepId: number;
  changeId: number;
  changeName: string;
  approvalId: number;
  approvalStatusId: number;
  approverId: number;
  approverName: string;
  approverEmail: string;
  approvalStatusName: string;
  notificationDateTime: Date | string;
  approvedDateTime: Date | string;
  comment?: string;
}

export interface ItemCABRequest {
  id: number;
  name: string;
  description: string;
  members: number[];
}

export interface IDuplicateChange {
  id: number;
  title: string;
  serviceName: string;
  configurationItems: string[];
  statusName: string;
  createdDateTime: string;
  similarityPercentage: number;
  similarityLevel: string;
}

export interface ICheckDuplicatesResponse {
  hasDuplicates: boolean;
  duplicateChanges: IDuplicateChange[];
  message: string;
}

export interface SearchTasksParams {
  releaseId?: number;
  TicketId?: number;
  problemId?: number;
  ChangeId?: number;
  PageNumber?: number;
  ChangeStageId?: number;
  OrderColumn?: string;
  Descending?: boolean;
  PageSize?: number;
  Text?: string;
  IsExport?: boolean;
}

export interface TaskResponse {
  id?: number;
  name?: string;
  description?: string;
  enabled?: boolean;
  taskStatusId?: number;
  taskStatusName?: string;
  taskTypeId?: number;
  taskTypeName?: string;
  ownerId?: number;
  ownerName?: string;
  startShceduledDate?: string;
  endScheduledDate?: string;
  progress?: number;
  startRealDate?: Date | string;
  endRealDate?: Date | string;
  estimatedDays?: number;
  estimatedHours?: number;
  estimatedMinutes?: number;
  priorityId?: number;
  priorityName?: string;
}

export interface CreationTask {
  id?: number;
  name?: string;
  taskStatusId?: number;
  description?: string;
  taskTypeId?: number;
  startScheduledDate?: Date;
  endScheduledDate?: Date;
  endscheduledDate?: Date;
  startRealDate?: Date;
  endRealDate?: Date;
  priorityId?: number;
  ticketId?: number;
  ownerId?: number;
  cost?: number | string;
  systemGroupId?: number;
  scheduleStartDate?: Date;
  estimatedDays?: number | string;
  estimatedHours?: number | string;
  estimatedMinutes?: number | string;
  progress?: number | string;
  comment?: string;
  problemId?: number;
  changeId?: number;
  changeStageId?: number;
}
export interface IChangeFlowParams {
  isBase?: boolean;
  ChangeFlowId?: number;
}

export interface IFlowOfChangeRequest {
  changeFlowId: number;
  enabled?: boolean;
  name: string;
  edges: {
    initialStageId: number;
    finalStageId: number;
    isInitialStage: boolean;
  }[];
}

export interface IItemFlowResponse {
  base: boolean;
  enabled: boolean;
  id: number;
  name: string;
}

export type TStatusesFlow = {
  id: 1;
  description: string;
  enabled: true;
  nextStageId?: number;
};

export type TStage = {
  id: number;
  description: string;
  enabled: boolean;
  statuses: TStatusesFlow[];
};

export interface IEdgeChangeFlow {
  initialStage: TStage;
  finalStage: TStage;
  isInitialStage: boolean;
  enabled: boolean;
}
export interface IFlowDiagramResponse {
  createdDateTime: Date | string;
  edges: IEdgeChangeFlow[];
  historical?: boolean;
  id: number;
  name: string;
  enabled: boolean;
  base: boolean;
}

export interface IStages {
  stage: TStage[];
}
export interface IItemFlowById {
  createdDateTime: Date | string;
  stages: IStages;
  historical?: boolean;
  id: number;
  name: string;
  enabled: boolean;
  base: boolean;
}

export interface EditTaskResponse {
  id?: number;
  name?: string;
  taskStatusId?: number;
  taskStatusName?: string;
  description?: string;
  taskTypeId?: number;
  taskTipeName?: string;
  startScheduledDate?: Date;
  endScheduledDate?: Date;
  startRealDate?: Date;
  endRealDate?: Date;
  priorityId?: number;
  priorityName?: string;
  ticketId?: number;
  ownerId?: number;
  ownerName?: string;
  cost?: number;
  systemGroupId?: number;
  estimatedDays?: number;
  estimatedHours?: number;
  estimatedMinutes?: number;
  progress?: number;
  comments?: Comment[];
}

export interface Comment {
  id?: number;
  comment?: string;
  initialStatusName?: string;
  finalStatusName?: string;
  createdByUserId?: number;
  createdByUserName?: string;
  createdDateTime?: Date;
}

export interface EditTaskRequest {
  id?: number;
  problemId?: number;
  name?: string;
  taskStatusId?: number;
  description?: string;
  taskTypeId?: number;
  startScheduledDate?: Date;
  endScheduledDate?: Date;
  startRealDate?: Date;
  endRealDate?: Date;
  priorityId?: number;
  ownerId?: number;
  cost?: number;
  estimatedDays?: number;
  estimatedHours?: number;
  estimatedMinutes?: number;
  progress?: number;
  comment?: string;
  enabled?: boolean;
}

export interface IModalValidationResquest {
  changeId: number;
  systemUserId?: number;
}

export interface IModalValidationResponse {
  showModal: boolean;
  approvalId: number;
  changeId: number;
  changeName: string;
}

export interface SearchSolutionsRequest {
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  pageSize?: number;
  text?: string;
  isExport?: boolean;
  enabled?: boolean;
  solutionStatusIds?: number[];
  toReview?: boolean;
  withoutReviewDate?: boolean;
  updatedDateTime?: Date;
  createdDateTime?: Date;
  createdUserId?: number;
  expirationDate?: Date;
  reviewDate?: Date;
  solutionId?: number;
  keywords?: string;
  ownerUserId?: number;
  topicId?: number;
  title?: string;
  updatedUserId?: number;
}

export interface SearchSolutionsResponse {
  id?: number;
  solutionId?: number;
  title?: string;
  topicId?: number;
  topicName?: string;
  keywords?: string;
  solutionStatusId?: number;
  solutionStatusName?: string;
  viewsCount?: number;
  createdUserId?: number;
  createdDateTime?: Date;
  updatedUserId?: number;
  updatedDateTime?: Date;
  reviewDate?: Date;
  expirationDate?: Date;
  ownerUserIds?: number[];
  solutionTypeId?: number;
}

export interface GetAllSolutionStatesResponse {
  id?: number;
  name?: string;
  enabled?: boolean;
  createdUserId?: number;
  createdDateTime?: Date;
  updatedUserId?: number;
  updatedDateTime?: string;
}

export interface ICommentChange {
  changeId: number;
  comment: string;
}

export interface IProductChange {
  changeId: number;
  productIds: number[];
}

export interface IChangeResponse {
  id: number;
  clientId: number;
  clientName: string;
  locationId: number;
  locationName: string;
  requestedEmail: string;
  requestedByUserId: number;
  requestedByUserName: string;
  changeTypeId: number;
  changeTypeName: string;
  changeServiceId: number;
  changeServiceName: string;
  categoryId: number;
  categoryName: string;
  subcategoryId: number;
  subcategoryName: string;
  assignmentPriorityId: number;
  assignmentPriorityName: string;
  engineerId: number;
  engineerName: string;
  assignedEngineerDateTime: null;
  ownerSystemUserId: number;
  ownerSystemUserName: string;
  isServiceAffected: true;
  title: string;
  description: string;
  background: string;
  downtimeHours: number;
  startDateTime: Date | string;
  endDateTime: Date | string;
  changeFlowId: number;
  changeFlowName: string;
  changeStageId: number;
  changeStageName: string;
  changeStatusId: number;
  changeStatusName: string;
  changeStepId: number;
  projectName?: string;
  urgencyId: number;
  impactId: number;
  products: {
    productId: number;
    serialNumber: string;
  }[];
  affectedProducts?: {
    productId: number;
    serialNumber: string;
  }[];
  comments: TCommentItem[];
  approvals: {
    id: number;
    approvalStatusName: string;
    changeStageName: string;
    systemUserName: string;
    systemUserId: number;
    approvalDateTime: Date | string;
    comment: string;
    createdByUserName: string;
    createdByUserId: number;
    createdDateTime: Date | string;
    approvalStatusId: number;
    systemUserAdvisoryBoards: {
      id: number;
      name: string;
    }[];
  }[];
  steps: {
    id: number;
    changeStageId: number;
    changeStageName: string;
    requireApproval: number;
  }[];
  createdDateTime?: Date | string;
  planningInfo?: {
    implementationPlan?: string;
    plannedStartDateTime?: Date;
    plannedEndDateTime?: Date;
    rollbackPlan?: string;
    rollbackCriteria?: string;
  };
  needPlanningInfo?: boolean;
  closeInformation?: string;
  reviewInformation?: string;
  isPreApproved?: boolean;
  cost?: number;
  currencyId?: number;
  unusualSkills?: string;
  specificTechnologies?: string;
  systemGroupId?: number;
  systemGroupName?: string;
  approvalStatus?: string;
}

export interface IProblemSearchRequest extends IParams {
  textType?: number;
  title?: string;
  description?: string;
  problemStatusIds?: number[];
  categoryIds?: number[];
  subcategoryIds?: number[];
  /** ID de producto/activo (string) */
  productId?: string;
  /** Serial del componente (exacto o parcial) */
  serialNumber?: string;
  /** 1 Reactiva, 2 Proactiva */
  identificationType?: number;
  rootCauseCatalogCategoryIds?: number[];
  isKnownError?: boolean;
  resolutionNotes?: string;
  hasRelatedIncidents?: boolean;
  relatedIncidentId?: string;
  /** 1 GreaterThan, 2 LessThan, 3 Equal */
  relatedIncidentCountOperator?: number;
  relatedIncidentCountValue?: number;
  hasWorkaroundTickets?: boolean;
  workaroundTicketId?: string;
  /** 1 GreaterThan, 2 LessThan, 3 Equal */
  workaroundTicketCountOperator?: number;
  workaroundTicketCountValue?: number;
  impactIds?: number[];
  urgencyIds?: number[];
  priorityIds?: number[];
  engineerIds?: number[];
  requesterIds?: number[];
  /** RN01. IDs de usuarios responsables (Owner) de tareas del problema */
  taskOwnerIds?: number[];
  /** RN02. IDs de proyectos (resueltos vía el cliente del problema) */
  projectIds?: number[];
  createdDateTimeStart?: Date | string;
  createdDateTimeEnd?: Date | string;
  startDateTimeStart?: Date | string;
  startDateTimeEnd?: Date | string;
  endDateTimeStart?: Date | string;
  endDateTimeEnd?: Date | string;
  myProblems?: boolean;
  /** true = solo archivados; false/omitido = no archivados (RN7) */
  isArchived?: boolean;
}

/** Request del historial de cambios (auditoría) de un problema */
export interface IProblemTrackingRequest extends IParams {
  problemId: number;
}

/** Entrada del historial de cambios (auditoría) de un problema */
export interface IProblemTrackingResponse {
  createdDate: Date | string;
  userName: string;
  userFullName: string;
  actionType: string;
  problemReason: string;
  updatedFields: string;
  problemStageId?: number | null;
  problemStageName?: string | null;
}

/** Selector Sí / No / sin filtro (solo UI) */
export type IProblemSearchTriBool = '' | 'yes' | 'no';

/** Valores de formulario de búsqueda de problemas (UI + campos que se mapean al request) */
export interface IProblemSearchFormValues extends IProblemSearchRequest {
  /** Alias usado por algunos layouts (barra de búsqueda) */
  search?: string;
  /** Categoría activo (UI); se mapea a categoryIds */
  categoryId?: number | null;
  /** Subcategoría activo (UI); se mapea a subcategoryIds */
  subCategoryId?: number | null;
  /** Categoría causa raíz (UI); se mapea a rootCauseCatalogCategoryIds */
  rootCauseCatalogCategoryId?: number | null;
  /** Incidentes relacionados: tri-estado → hasRelatedIncidents */
  relatedIncidentsTri?: IProblemSearchTriBool;
  /** Workaround: tri-estado → hasWorkaroundTickets */
  workaroundTicketsTri?: IProblemSearchTriBool;
  /** Error conocido: tri-estado → isKnownError */
  knownErrorTri?: IProblemSearchTriBool;
  /** Archivado: tri-estado → isArchived */
  archivedTri?: IProblemSearchTriBool;
  /** Tipo de causa raíz (UI); se mapea a rootCauseCauseType */
  rootCauseCauseType?: number | null;
}

export interface IProblemCategory {
  id: number;
  name: string;
  createdUserId: number;
  createdDateTime: Date | string;
  updatedUserId: null;
  updatedDateTime: null;
  enabled: boolean;
  rowVersion: string;
  description: string;
  parentCategoryId: number;
  createdUser: null;
  parentCategory: null;
  updatedUser: null;
  changes: [];
  inverseParentCategory: [];
  tickets: [];
  problemCategories: [];
  problemSubCategories: [];
}
export interface IProblemSearchResponse {
  id: number;
  title: string;
  problemStatusId: number;
  problemStatusName: string;
  createdDateTime: Date | string;
  createdUserId: number;
  categoryId: number;
  categoryName: string;
  subcategoryId: IProblemCategory;
  subcategoryName: string;
  priorityId: number;
  priorityName: string;
  urgencyId: number;
  urgencyName: string;
  impactId: number;
  impactName: 'Bajo';
  engineerId: number;
  engineerName: string;
  requesterId: number;
  requesterName: string;
  description: string;
  endDateTime: Date | string;
  knownIssue: null;
  knownIssueDetails: [];
  clientId?: number;
  clientName?: string;
  /** RN02. Proyecto (resuelto vía cliente) */
  projectId?: number;
  projectName?: string;
  problemStageId?: number;
  problemStageName?: string;
  isArchived?: boolean;
}

export interface ICreateProblem {
  id?: number;
  title: string;
  version?: string;
  description?: string;
  categoryId?: number;
  subCategoryId?: number;
  impactId?: number;
  urgencyId?: number;
  priorityId?: number;
  riskId?: number;
  typeId?: number;
  groupId?: number;
  statusId?: number;
  requestedByUserId?: number;
  ownerId?: number;
  engineerId?: number;
  endDateTime?: Dayjs | string;
  startDate?: Dayjs | string;
  targetEndDate?: Dayjs | string;
  hasRollback?: boolean;
  rollbackStart?: Dayjs | string;
  rollbackEnd?: Dayjs | string;
  rollbackDuration?: number;
  rollbackDetail?: string;
  productIds?: number[];
  products?: {
    productId: number;
    serialNumber: string;
  }[];
  locationIds?: number[] | null;
  clientId?: number;
  templateId?: number;
  formTemplateId?: number;
  itemsCustom?: Array<{
    formFieldId: number;
    value: string;
    rowGuid: string;
  }>;
  identificationType?: number;
  proactiveJustification?: string;
  justificationLocationReason?: string;
  justificationMotiveReason?: string;
  /** Causa raíz (catálogo). En actualización: null = no modificar */
  rootCauseCatalogId?: number | null;
  isTechnicalCause?: boolean | null;
  rootCauseCatalogCategoryId?: number | null;
  rootCauseCatalogSubCategoryId?: number | null;
}

export interface NewProblemCreate {
  /** 1 = técnica, 2 = no técnica */
  rootCauseCauseType?: number;
  isTechnicalCause?: boolean;
  rootCauseCatalogId?: number;
  title: string;
  description?: string;
  categoryId?: number;
  subCategoryId?: number;
  statusId?: number;
  requestedByUserId?: number;
  productIds?: number[];
  products?: {
    productId: number;
    serialNumber: string;
  }[];
  locationIds?: number[] | null;
  clientId?: number;
}

export interface IKnownIssueDetails {
  id?: number;
  updataKnownIssue?: Date | string;
  commentKnownIssue?: string;
  updatedUserId?: number;
  updatedUserName?: string;
}

export interface IProblemAnalysis {
  sectionId?: number;
  sectionName?: string;
  displayOrder?: number;
  note?: INote;
}
export interface IReleaseDetail {
  id?: number;
  title?: string;
  description?: string;
  version?: string;
  releaseStatusId?: number;
  releaseStatusName?: string;
  ownerSystemUserId?: number;
  ownerName?: string;
  requestedByUserId?: number;
  requestedByUserName?: string;
  engineerId?: number;
  engineerName?: string;
  groupId?: number;
  groupName?: string;
  urgencyId?: number;
  urgencyName?: string;
  impactId?: number;
  impactName?: string;
  priorityId?: number;
  priorityName?: string;
  categoryId?: number;
  categoryName?: string;
  subCategoryId?: number;
  subCategoryName?: string;
  riskId?: number;
  riskName?: string;
  typeId?: number;
  typeName?: string;
  plannedStartDateTime?: Date;
  plannedEndDateTime?: Date;
  hasRollback?: boolean;
  rollbackStartDateTime?: Date;
  rollbackEndDateTime?: null;
  rollbackDurationHours?: number;
  rollbackDetails?: string;
  clientId?: number;
  clientName?: string;
  products?: Array<{
    productId: number;
    serialNumber: string;
  }>;
  dynamicFormFields?: Array<{
    formFieldId?: number;
    fieldName?: string;
    value?: string | null;
    fieldIdOption?: number | null;
    options?: string[];
  }>;
  formTemplateId?: number;
  notes?: unknown[];
  createdUserId?: number;
  createdUserName?: string;
  createdDateTime?: Date;
  updatedUserId?: null;
  updatedUserName?: null;
  updatedDateTime?: null;
  rowVersion?: string;
}

export interface ProblemTemplate {
  id?: number;
  name?: string;
  order?: number;
  columnCount?: number;
  items?: Item[];
}

export interface Item {
  id?: number;
  name?: string;
  fieldTypeName?: string;
  required?: boolean;
  itemOrder?: number;
  fixedPosition?: number;
  isFullSize?: boolean;
  isDefault?: boolean;
  placeholder?: null;
  sectionId?: number;
  sectionName?: string;
  sectionOrder?: number;
  columnCount?: number;
  sectionFieldOrder?: number;
  columnPosition?: null;
  options?: null;
  requireComponentes?: boolean;
  value?: null | string;
  fieldIdOption?: null;
}

export interface IProblemDetail {
  id?: number;
  clientId?: number;
  clientName?: string;
  requestedByUserId?: number;
  requestedByUserName?: string;
  categoryId?: number;
  categoryName?: string;
  subcategoryId?: number;
  subcategoryName?: string;
  /** Categoría de activo/ticket (Problem.CategoryId), distinta de la categoría de causa raíz en `categoryId` cuando hay catálogo. */
  ticketCategoryId?: number;
  ticketSubCategoryId?: number;
  ticketCategoryName?: string;
  ticketSubCategoryName?: string;
  engineerId?: number;
  engineerName?: string;
  engineerDateTime?: Date;
  title?: string;
  description?: string;
  endDateTime?: Date;
  problemStatusId?: number;
  problemStatusName?: string;
  priorityId?: number;
  priorityName?: string;
  urgencyId?: number;
  urgencyName?: string;
  impactId?: number;
  impactName?: string;
  products?: unknown[];
  knownIssue?: boolean;
  knownIssueDetails?: IKnownIssueDetails[];
  createdDateTime?: Date;
  groupId?: number;
  groupName?: string;
  notes?: unknown[];
  problemAnalysis?: IProblemAnalysis[];
  isPrimarySolution?: null;
  tickets?: {
    ticketId?: number;
    description?: null;
    requestedByUserId?: null;
    requestedByUser?: null;
    assignedEnginnerId?: null;
    assignedEnginner?: null;
    endDateTime?: Date;
    statusId?: number;
    status?: string;
    createdDateTime?: Date;
    isWorkaround?: boolean;
    workaroundResult?: number;
  }[];
  templateFields?: Array<{
    formFieldId?: number;
    fieldName?: string;
    fieldTypeId?: number;
    fieldTypeName?: string;
    fixedPosition?: number;
    required?: boolean;
    itemOrder?: number;
    isFullSize?: boolean;
    isDefault?: boolean;
  }>;
  dynamicFormFields?: Array<{
    formFieldId?: number;
    fieldName?: string;
    value?: string | null;
    fieldIdOption?: number | null;
    options?: string[];
  }>;
  dynamicFormFieldsKeyVault?: { [key: string]: string };
  formTemplateId?: number;
  sections?: ProblemTemplate[];
  identificationType?: number;
  identificationTypeName?: string;
  proactiveJustification?: string;
  justificationLocationReason?: string;
  justificationMotiveReason?: string;
  locationIds?: number[] | null;
  locations?: Array<{ id?: number; name?: string; label?: string }>;
  stageId?: number;
  problemStageId?: number;
  problemStageName?: string;
  hasExpertBoard?: boolean;
  boardAssignments?: IProblemBoardAssignment[];
  isKnownError?: boolean;
  rootCauseCatalogId?: number;
  isTechnicalCause?: boolean;
  rootCauseCatalogCategoryId?: number;
  rootCauseCatalogSubCategoryId?: number;
  /** Nombres para UI/autocomplete cuando el backend los envía junto al detalle */
  rootCauseCatalogCategoryName?: string;
  rootCauseCatalogSubCategoryName?: string;
  fiveWhys?: IFiveWhyItem[];
  ishikawa?: {
    diagramId?: number;
    problemDescription?: string;
    categories?: IIshikawaCategory[];
  };
  // Campos específicos para Etapa 4: Control de errores
  resolutionStatus?: number; // 1=Resuelto, 2=ResueltoConErrores, 3=NoResuelto
  resolutionNotes?: string;
  /** SAIMONOPE-898: problema archivado (detalle solo lectura) */
  isArchived?: boolean;
}

export interface IArchiveProblemRequest {
  problemId: number;
  /** true = archivar; false = desarchivar */
  isArchived: boolean;
}

export interface IProblemBoardAssignment {
  id: number;
  problemExpertBoardId?: number;
  problemExpertBoardName?: string;
  systemUserId?: number;
  systemUserName?: string;
}

export interface IChangeProblemStageRequest {
  problemId: number;
  newStageId: number;
  comments?: string;
}

export interface ISetProblemKnownIssue {
  isKnownIssue: boolean;
  problemId: number;
  comment: string;
}

/* ── Etapa 3: Control de Problemas ── */

export interface IFiveWhyItem {
  id?: number;
  whyNumber: number;
  question?: string;
  answer?: string;
}

export interface IGetFiveWhysResponse {
  problemId: number;
  items: IFiveWhyItem[];
}

export interface ISaveFiveWhysRequest {
  problemId: number;
  items: Array<{ whyNumber: number; question?: string; answer?: string }>;
}

export interface ISaveFiveWhysResponse {
  problemId: number;
  savedCount: number;
}

export interface IIshikawaCause {
  id?: number;
  description: string;
  itemOrder?: number;
}

export interface IIshikawaCategoryItem {
  id: number;
  name: string;
  itemOrder?: number;
}

export interface IIshikawaCategory {
  id: number;
  name: string;
  causes: IIshikawaCause[];
}

export interface IGetIshikawaCategoriesResponse {
  categories: IIshikawaCategoryItem[];
}

export interface IGetIshikawaResponse {
  problemId: number;
  diagramId?: number;
  problemDescription?: string;
  categories: IIshikawaCategory[];
}

export interface ISaveIshikawaRequest {
  problemDescription?: string;
  categories: Array<{ categoryId: number; causes: IIshikawaCause[] }>;
}

export interface ISaveIshikawaResponse {
  problemId: number;
  diagramId: number;
}

export interface IProblemCategorySurveyQuestion {
  surveyQuestionTemplateId: number;
  surveyTemplateId: number;
  label: string;
  isRequired: boolean;
  orderItem: number;
  options?: string;
  typeComponent: string;
  maxLenght?: number;
  level: 'category' | 'subcategory';
}

export interface IGetProblemCategorySurveyQuestionsResponse {
  problemId: number;
  questions: IProblemCategorySurveyQuestion[];
}

export interface IProblemCategorySurveyAnswer {
  surveyQuestionTemplateId: number;
  answerValue: string;
}

export interface IGetProblemCategorySurveyAnswersResponse {
  problemId: number;
  answers: IProblemCategorySurveyAnswer[];
}

export interface ISaveProblemCategorySurveyAnswersRequest {
  answers: Array<{
    surveyQuestionTemplateId: number;
    answerValue?: string;
  }>;
}

export interface ISaveControlStageRequest {
  isKnownError?: boolean;
  rootCauseCatalogId?: number;
  isTechnicalCause?: boolean;
  fiveWhys?: Array<{ whyNumber: number; question?: string; answer?: string }>;
  ishikawa?: ISaveIshikawaRequest;
  categorySurveyAnswers?: Array<{
    surveyQuestionTemplateId: number;
    answerValue: string;
  }>;
  // Campos específicos para Etapa 4: Control de errores
  resolutionStatus?: number; // 1=Resuelto, 2=ResueltoConErrores, 3=NoResuelto
  resolutionNotes?: string;
}

export interface ISaveControlStageResponse {
  problemId: number;
  saved: boolean;
}

export interface IGetCanAdvanceFromControlResponse {
  canAdvance: boolean;
  tasksNotComplete: boolean;
  pendingTasksCount: number;
  analysisOrCauseMissing: boolean;
  workaroundPending: boolean;
  workaroundAllFailed: boolean;
}

export interface IAssociateWorkaroundTicketRequest {
  locationId: number;
  serviceId: number;
  comment: string;
  remote: boolean;
  assignedEngineerId: number;
  problemId: number;
}

export interface IAssociateWorkaroundTicketResponse {
  problemId: number;
  ticketId: number;
  problemTicketId: number;
}

export interface IControlStageTipsResponse {
  tips: string[];
}

export interface IControlStageExportData {
  problemId: number;
  title: string;
  description: string;
  problemStageName: string;
  isKnownError: boolean;
  rootCauseCatalogId?: number;
  isTechnicalCause?: boolean;
  fiveWhys?: IFiveWhyItem[];
  ishikawa?: IGetIshikawaResponse;
  workarounds?: unknown[];
}

// ================== ETAPA 4: CONTROL DE ERRORES ==================

export interface ISaveErrorControlStageRequest {
  problemId: number;
  resolutionStatus?: number; // 1=Resuelto, 2=ResueltoConErrores, 3=NoResuelto
  resolutionNotes?: string;
  rootCauseCatalogId?: number;
  isTechnicalCause?: boolean;
}

export interface IFinalizeProblemRequest {
  problemId: number;
  resolutionStatus: number;
  resolutionNotes?: string;
  finalStatusId: number;
  closeAssociatedTickets?: boolean;
}

export interface IWorkaroundHistoryItem {
  ticketId: number;
  createdDateTime: string;
  requestedByUser: string;
  assignedEnginner: string;
  status: string;
  workaroundResult: number; // 1=Pendiente, 2=Exitoso, 3=Fallido
  endDateTime?: string;
  requestedByUserId?: number;
  assignedEnginnerId?: number;
  statusId?: number;
  isWorkaround: boolean;
}

export interface IFinalizePreviewTicketItem {
  ticketId: number;
  title: string;
  status: string;
  isClosed: boolean;
}

export interface IGetFinalizeProblemPreviewResponse {
  problemId: number;
  hasAssociatedTickets: boolean;
  associatedTickets: IFinalizePreviewTicketItem[];
}

export interface ICloseRelatedTicketRequest {
  comment?: string;
}

export interface ICloseRelatedTicketResponse {
  problemId: number;
  ticketId: number;
  closed: boolean;
}

export interface ISaveErrorControlStageResponse {
  problemId: number;
  saved: boolean;
}

export interface IProblemStageProgress {
  stageId: number;
  stageName: string;
  isCurrent: boolean;
  isCompleted: boolean;
  order: number;
}

export interface IGetProblemProgressResponse {
  breadcrumbStages: IProblemStageProgress[];
  problemSummary: {
    problemId: number;
    title: string;
    currentStageId: number;
    currentStageName: string;
    hasExpertBoard: boolean;
    isKnownError: boolean;
  };
  availableStages: Array<{
    stageId: number;
    stageName: string;
    isEnabled: boolean;
    order: number;
  }>;
}

export interface IUpdateProblemStatus {
  problemId: number;
  finalStatusId: number;
}

export interface IAssignProblem {
  problemId: number;
  groupId: number;
  engineerUserId: number;
}

export interface IProblemExpertBoardMember {
  id: number;
  systemUserId: number;
  systemUserName?: string;
  fullName?: string;
  area?: string;
  email?: string;
}

export interface IProblemExpertBoardResponse {
  id: number;
  name: string;
  description?: string;
  memberCount?: number;
  members?: IProblemExpertBoardMember[];
}

export interface IProblemExpertBoardCreateRequest {
  name: string;
  description?: string;
  members: number[];
}

export interface IProblemExpertBoardUpdateRequest
  extends IProblemExpertBoardCreateRequest {
  id: number;
}

export interface ISaveProblemCommitteeRequest {
  expertBoardIds: number[];
  individualExpertIds: number[];
}

export interface IGetCanAdvanceFromComiteStageResponse {
  canAdvance: boolean;
  committeeMissing: boolean;
  hasPendingTasks: boolean;
  pendingTasksCount: number;
}

export interface IUpdateProblemPriority {
  problemId: number;
  priorityId: number;
}

export interface IReleaseSearchRequest extends IParams {
  textType?: number;
  title?: string;
  releaseStatusIds?: number[];
  categoryIds?: number[];
  subcategoryIds?: number[];
  impactIds?: number[];
  urgencyIds?: number[];
  priorityIds?: number[];
  riskIds?: number[];
  typeIds?: number[];
  engineerIds?: number[];
  ownerIds?: number[];
  requesterIds?: number[];
  createdDateTimeStart?: Date | string;
  createdDateTimeEnd?: Date | string;
  startDateTimeStart?: Date | string;
  startDateTimeEnd?: Date | string;
  endDateTimeStart?: Date | string;
  endDateTimeEnd?: Date | string;
  myReleases?: boolean;
  userEmail?: string;
  UserRole?: string;
  clientId?: number;
}

export interface IReleaseSearchResponse {
  id: number;
  title: string;
  version?: string;
  releaseStatusId: number;
  releaseStatusName: string;
  createdDateTime: Date | string;
  createdUserId: number;
  categoryId: number;
  categoryName: string;
  subcategoryId: number;
  subcategoryName: string;
  priorityId: number;
  priorityName: string;
  urgencyId: number;
  urgencyName: string;
  impactId: number;
  impactName: string;
  riskId: number;
  riskName: string;
  typeId: number;
  typeName: string;
  engineerId: number;
  engineerName: string;
  ownerId: number;
  ownerName: string;
  requesterId: number;
  requesterName: string;
  description: string;
  startDate?: Date | string;
  targetEndDate?: Date | string;
  endDateTime: Date | string;
  hasRollback?: boolean;
  rollbackStart?: Date | string;
  rollbackEnd?: Date | string;
  rollbackDuration?: number;
  rollbackDetail?: string;
}

export interface ICreateRelease {
  id?: number;
  title: string;
  version?: string;
  description?: string;
  categoryId?: number;
  subCategoryId?: number;
  impactId?: number;
  urgencyId?: number;
  priorityId?: number;
  riskId?: number;
  typeId?: number;
  groupId?: number;
  statusId?: number;
  releaseStatusId?: number;
  requestedByUserId?: number;
  ownerId?: number;
  ownerSystemUserId?: number;
  engineerId?: number;
  plannedStartDateTime?: Dayjs | string;
  plannedEndDateTime?: Dayjs | string;
  startDate?: Dayjs | string;
  targetEndDate?: Dayjs | string;
  hasRollback?: boolean;
  rollbackStart?: Dayjs | string;
  rollbackEnd?: Dayjs | string;
  rollbackStartDateTime?: Dayjs | string;
  rollbackEndDateTime?: Dayjs | string;
  rollbackDuration?: number;
  rollbackDurationHours?: number;
  rollbackDetail?: string;
  rollbackDetails?: string;
  productIds?: number[];
  products?: {
    productId: number;
    serialNumber: string;
  }[];
  clientId?: number;
  templateId?: number;
  formTemplateId?: number;
  itemsCustom?: Array<{
    formFieldId: number;
    value: string;
    rowGuid: string;
  }>;
  changeId?: number;
}

export interface IUpdateReleaseStatus {
  releaseId: number;
  finalStatusId: number;
}

export interface IAssignRelease {
  releaseId: number;
  groupId: number;
  engineerUserId: number;
}

export interface IUpdateReleasePriority {
  releaseId: number;
  priorityId: number;
}

export interface NewSolutionRequest {
  solutionTypeId?: number;
  title?: string;
  content?: string;
  solutionTopicId?: number;
  ownerSystemUserIds?: number[];
  reviewDate?: Date;
  expirationDate?: Date;
  keywords?: string;
  internalComments?: string;
}

export interface SolutionByIDResponse {
  id?: number;
  solutionTypeId?: number;
  title?: string;
  content?: string;
  topicId?: number;
  topicName?: string;
  solutionStatusId?: number;
  solutionStatusName?: string;
  ownerSystemUserIds?: number;
  ownerSystemUsersData?: {
    systemUserId: number;
    systemUserFullName: string;
  }[];
  reviewDate?: Date;
  expirationDate?: Date;
  viewsCount?: number;
  createdByUserName?: string;
  createdDateTime?: Date;
  updatedByUserName?: null;
  updatedDateTime?: null;
  requestsCount?: number;
  problemsCount?: number;
  solutionsCount?: number;
  keywords?: string;
  internalComments?: string;
}

export interface SolutionUpdateRequest {
  id?: number;
  title?: string;
  content?: string;
  solutionTopicId?: number;
  ownerSystemUserIds?: number[];
  reviewDate?: Date;
  expirationDate?: Date;
  keywords?: string;
  internalComments?: string;
}

export interface IRelatedProblem {
  solutionId: number;
  problemIds: number[];
  solutionTypeId?: number;
}

export interface IRelatedTicketProblem {
  ticketIds: number[];
  problemId: number;
}

export interface IRelatedProblemChange {
  problemIds: number[];
  changeId: number;
}

export interface IRelatedTicketChange {
  ticketIds: number[];
  changeId: number;
}

export interface GetProblemsByChangeRequest {
  changeId: number;
  pageNumber?: number;
  pageSize?: number;
  orderColumn?: string;
  descending?: boolean;
  text?: string;
  isExport?: boolean;
}

export interface GetTicketsByChangeRequest {
  changeId: number;
  pageNumber?: number;
  pageSize?: number;
  orderColumn?: string;
  descending?: boolean;
  text?: string;
  isExport?: boolean;
}

export interface IAnalysisSection {
  id: number;
  name: string;
  description: string;
  displayOrder: number;
}

export interface IFileNote {
  fileId: number;
  fileName: string;
  fileDescription: string;
  blobStorageUrlPath: string;
}

export interface INote {
  noteId: number;
  comment: string;
  createdUserId: number;
  createdUserName: string;
  createdDateTime: Date | string;
  updatedDateTime: Date | string;
  updatedUserName: string;
  analysisSectionId: number;
  analysisSectionName: string;
  files?: IFileNote[];
}

export interface IAnalysisSectionNotes {
  problemId: number;
  notes: INote[];
}

export interface ICreateProblemNote {
  comment: string;
  problemId: number;
  analysisSectionId?: number;
}

export interface IUpdateProblemNote {
  comment: string;
  noteId: number;
}

export interface ITaskByProblemId {
  totalTask: number;
  completeTask: number;
}

export interface TaskOrderProblem {
  id?: number;
  name?: string;
  itemOrder?: number;
}

export interface NewTaskOrderProblem {
  taskOrders: [
    {
      id?: number;
      order?: number;
    }
  ];
}

export interface CreateTaskByTemplate {
  problemId?: number;
  changeId?: number;
  changeStageId?: number;
  taskTemplateIds?: number[];
}

export interface ProblemFlowSearchRequest {
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  pageSize?: number;
  text?: string;
  isExport?: boolean;
  enabled?: boolean;
}

export interface ProblemFlowSearch {
  id?: number;
  name?: string;
  enabled?: boolean;
  base?: boolean;
}

export interface ProblemFlowSearchIDResponse {
  createdDateTime?: Date;
  edges?: Edge[];
  historical?: boolean;
  id?: number;
  name?: string;
  enabled?: boolean;
  base?: boolean;
  clientId?: number;
}

export interface Edge {
  initialStatus?: AlStatus;
  finalStatus?: AlStatus;
  isInitialStatus?: boolean;
  enabled?: boolean;
  hideForSelection?: boolean;
}

export interface AlStatus {
  id?: number;
  description?: string;
  enabled?: boolean;
  nextStatusId?: number;
}

export interface CreateProblemFlowRequest {
  name?: string;
  edges?: Edge[];
  clientId?: number;
}

export interface ProblemStatusSearchRequest {
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  pageSize?: number;
  text?: string;
  isExport?: boolean;
  enabled?: boolean;
  base?: boolean;
  problemStatusId?: number;
  categoryId?: number;
  name?: string;
  description?: string;
}

export interface ProblemStatusResponse {
  problemStatusId?: number;
  name?: string;
  description?: null | string;
  categoryId?: number;
  categoryName?: string;
  base?: boolean;
  enabled?: boolean;
  isCompleted?: boolean;
}

export interface ProblemStatusByClientResponse {
  id?: number;
  name?: string;
  enabled?: boolean;
}

export interface CreateProblemStatusRequest {
  name?: string;
  description?: string;
  categoryId?: number;
  base?: boolean;
  isCompleted?: boolean;
}

export interface EditProblemStatusRequest {
  id?: number;
  name?: string;
  description?: string;
  categoryId?: number;
  base?: boolean;
  enabled?: boolean;
  isCompleted?: boolean;
}

export interface UseGetAllProblemStatus {
  id?: number;
  name?: string;
  description?: string;
  categoryId?: number;
  categoryName?: string;
  base?: null;
  enabled?: boolean;
}

export interface UpdateProblemFlow {
  problemFlowId?: number;
  name?: string;
  enabled?: boolean;
  clientId?: number;
  edges?: Edge[];
}

export interface GetAllProblemCategories {
  id?: number;
  name?: string;
  canEditOrDelete?: boolean;
  problemStatuses?: ProblemStatus[];
}

export interface ProblemStatus {
  id?: number;
  name?: string;
  description?: null | string;
  base?: boolean | null;
  enabled?: boolean;
}

export interface GetClientsWhitOutFlow {
  id?: number;
  name?: string;
}

export interface SolutionTypeRequest {
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  pageSize?: number;
  text?: string;
  isExport?: boolean;
  name?: string;
  description?: string;
  enabled?: boolean;
}

export interface SolutionTypeResponse {
  id?: number;
  name?: string;
  description?: string;
  enabled?: boolean;
  createdDateTime?: Date;
  createdUserId?: number;
  updatedDateTime?: null;
  updatedUserId?: null;
}

export interface SolutionTypeEditRequest {
  id?: number;
  name?: string;
  description?: string;
  enabled?: boolean;
}

export interface SolutionTypeCreateRequest {
  name?: string;
  description?: string;
  enabled?: boolean;
}

export interface UpdateChangeStageRequest {
  changeId?: number;
  stageId?: number;
  statusId?: number;
  comment?: string;
}

export interface IUpdateChangeStatus {
  changeId?: number;
  finalStatusId?: number;
}

export interface IChangeStatusAvailable {
  id: number;
  name: string;
}

export interface IPIRResponse {
  pirs?: Pir[];
}

export interface Pir {
  id?: number;
  changeId?: number;
  implementationResultId?: number;
  implementationResultName?: string;
  synopsis?: string;
  stepsToAvoidRepetition?: string;
  statusId?: number;
  statusName?: string;
  clientImpact?: string;
  rootCause?: string;
  startDateTime?: Date;
  endDateTime?: Date;
  createdDateTime?: Date;
  updatedDateTime?: null;
}

export interface ICreatePIRRequest {
  changeId?: number;
  implementationResultId?: number;
  synopsis?: string;
  stepsToAvoidRepetition?: string;
  statusId?: number;
  clientImpact?: string;
  rootCause?: string;
  startDateTime?: Date;
  endDateTime?: Date;
}

export interface IUpdatePIRRequest extends ICreatePIRRequest {
  id: number;
}

export interface IPIRStatusResponse {
  id?: number;
  name?: string;
  description?: string;
  enabled?: boolean;
}

export interface ITaskByChangeId {
  totalTask: number;
  completeTask: number;
}

export interface TaskOrderChange {
  id?: number;
  name?: string;
  itemOrder?: number;
}

export interface NewTaskOrderChange {
  taskOrders: [
    {
      id?: number;
      order?: number;
    }
  ];
}

export interface ISendPlanificationData {
  changeId?: number;
  plannedStartDateTime?: Date;
  plannedEndDateTime?: Date;
  maintenanceWindows?: string;
  implementationPlan?: string;
  rollbackPlan?: string;
  rollbackCriteria?: string;
}

export interface ChangeFlowAvailableStages {
  id?: number;
  description?: string;
  name?: string;
  statuses?: ChangeFlowAvailableStages[];
}

export interface TaskResponseInChange {
  data?: Data;
}

export interface Data {
  tasks?: Task[];
}

export interface Task {
  id?: number;
  name?: string;
  taskStatusId?: number;
  taskStatusName?: string;
  description?: string;
  taskTypeId?: number;
  taskTypeName?: string;
  startScheduledDate?: null;
  endScheduledDate?: null;
  startRealDate?: null;
  endRealDate?: null;
  priorityId?: number;
  priorityName?: string;
  ownerId?: number;
  systemGroupId?: number;
  systemGroupName?: string;
  ownerName?: string;
  cost?: number;
  estimatedDays?: number;
  estimatedHours?: number;
  estimatedMinutes?: number;
  progress?: number;
  changeStageId?: number;
  comments?: Comment[];
}

export interface ISendClosureData {
  changeId?: number;
  closureRelevantInfo?: string;
}

export interface ISendReviewData {
  changeId?: number;
  reviewInformation?: string;
}

export interface ReleaseStatusResponse {
  releaseStatusId?: number;
  name?: string;
  description?: string;
  itemOrder?: number;
  isCompleted?: boolean;
  categoryId?: number;
  categoryName?: string;
  enabled?: boolean;
}

export interface GetAllReleaseStatusResponse {
  id?: number;
  name?: string;
  description?: string;
  itemOrder?: number;
  isCompleted?: boolean;
  categoryId?: number;
  categoryName?: string;
  enabled?: boolean;
}

export interface CreateReleaseStatusRequest {
  name?: string;
  description?: string;
  itemOrder?: number;
  isCompleted?: boolean;
}

export interface EditReleaseStatusRequest {
  id?: number;
  name?: string;
  description?: string;
  itemOrder?: number;
  isCompleted?: boolean;
}

export interface ReleaseFlowSearch {
  id?: number;
  name?: string;
  enabled?: boolean;
  base?: boolean;
  clientId?: null;
  clientName?: null;
}

export interface CreateReleaseFlowRequest {
  name?: string;
  edges?: Edge[];
  clientId?: number;
}

export interface UpdateReleaseFlow {
  releaseFlowId?: number;
  name?: string;
  enabled?: boolean;
  clientId?: number;
  edges?: Edge[];
}

export interface ReleaseStatusCategories {
  id?: number;
  name?: string;
  canEditOrDelete?: boolean;
  releaseStatuses?: ReleaseStatus[];
}

export interface ReleaseStatus {
  id?: number;
  name?: string;
  description?: string;
  itemOrder?: number;
  isCompleted?: boolean;
  enabled?: boolean;
}

export interface SearchReleaseByChangeIdResponse {
  releaseId?: number;
  title?: string;
  version?: string;
  releaseStatusId?: number;
  releaseStatusName?: string;
  plannedStartDateTime?: Date;
  plannedEndDateTime?: Date;
  isActive?: boolean;
}

export interface ICreateReleaseNote {
  releaseId: number;
  comment: string;
}

export interface CreateTaskRelease {
  name?: string;
  taskStatusId?: number;
  description?: string;
  taskTypeId?: number;
  startScheduledDate?: Date;
  endScheduledDate?: Date;
  startRealDate?: Date;
  endRealDate?: Date;
  priorityId?: number;
  ownerId?: number;
  systemGroupId?: number;
  cost?: number;
  estimatedDays?: number;
  estimatedHours?: number;
  estimatedMinutes?: number;
  progress?: number;
  comment?: string;
  releaseId?: number;
}

export interface TaskReleaseResponse {
  data?: TaskReleaseData;
  message?: string;
  isError?: boolean;
}

export interface TaskReleaseData {
  tasks?: TaskReleaseItem[];
}

export interface TaskReleaseItem {
  id?: number;
  name?: string;
  taskStatusId?: number;
  taskStatusName?: string;
  description?: string;
  taskTypeId?: number;
  taskTypeName?: string;
  startScheduledDate?: Date | null;
  endScheduledDate?: Date | null;
  startRealDate?: Date | null;
  endRealDate?: Date | null;
  priorityId?: number | null;
  priorityName?: null | string;
  ownerId?: number | null;
  systemGroupId?: number | null;
  systemGroupName?: null | string;
  ownerName?: null | string;
  cost?: number | null;
  estimatedDays?: number | null;
  estimatedHours?: number | null;
  estimatedMinutes?: number | null;
  progress?: number | null;
  comments?: Comment[];
}

export interface AffectedAndLinkedProducts {
  primaryProducts?: Product[];
  dependentProducts?: Product[];
}

export interface Product {
  id?: number;
  serialNumber?: string;
  name?: string;
}

export default {
  instance,
  changeAdvisoryBoard: {
    /**
     * Obtiene todos los comités activos.
     *
     * @tags ChangeAdvisoryBoard
     * @name getAll
     * @request GET: /api/v1/ChangeAdvisoryBoard
     * @secure
     */
    getAll: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ItemCABResponse[]>>({
        url: `/ChangeAdvisoryBoard`,
        method: 'GET',
        ...config
      }),
    /**
     * Búsqueda de comités
     *
     * @tags SearchChangeAdvisoryBoard
     * @name SearchChangeAdvisoryBoard
     * @request POST: /api/v1/ChangeAdvisoryBoard/SearchChangeAdvisoryBoard
     * @secure
     */
    searchChangeAdvisoryBoard: (
      params: IParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<ItemCABResponse>>>({
        url: `/ChangeAdvisoryBoard/SearchChangeAdvisoryBoard`,
        method: 'POST',
        params,
        ...config
      }),
    /**
     * Crea un comité.
     *
     * @tags SearchChangeAdvisoryBoard
     * @name SearchChangeAdvisoryBoard
     * @request POST: /api/v1/ChangeAdvisoryBoard
     * @secure
     */
    create: (data: ItemCABRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ItemCABResponse>>({
        url: `/ChangeAdvisoryBoard`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Actualiza un comité.
     *
     * @tags SearchChangeAdvisoryBoard
     * @name SearchChangeAdvisoryBoard
     * @request PUT: /api/v1/ChangeAdvisoryBoard
     * @secure
     */
    update: (data: ItemCABRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ItemCABResponse>>({
        url: `/ChangeAdvisoryBoard`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Obtiene un Comité por su ID.
     *
     * @tags SearchChangeAdvisoryBoard
     * @name SearchChangeAdvisoryBoard
     * @request GET: /api/v1/ChangeAdvisoryBoard/{id}
     * @secure
     */
    getById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ItemCABResponse>>({
        url: `/ChangeAdvisoryBoard/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Elimina un comité por su ID.
     *
     * @tags SearchChangeAdvisoryBoard
     * @name SearchChangeAdvisoryBoard
     * @request DELETE: /api/v1/ChangeAdvisoryBoard/{id}
     * @secure
     */
    delete: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ItemCABResponse>>({
        url: `/ChangeAdvisoryBoard/${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Asigna un comité a un cambio.
     *
     * @tags SearchChangeAdvisoryBoard
     * @name AssignCABToChange
     * @request PUT: /api/v1/ChangeAdvisoryBoard/AssignCABToChange
     * @secure
     */
    AssignCABToChange: (
      data: { changeId: number; changeAdvisoryBoardId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/ChangeAdvisoryBoard/AssignCABToChange`,
        method: 'PUT',
        data,
        ...config
      })
  },
  approvals: {
    /**
     * Search approvals
     *
     * @tags Approvals
     * @name searchApprovals
     * @request GET: /api/v1/Approvals/SearchApprovals
     * @secure
     */
    SearchApprovals: (
      params: IParamsSearchApprovals,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<IChangesApprovalItem>>
      >({
        url: `/Approvals/SearchApprovals`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Get approvals by change step id
     *
     * @tags Approvals
     * @name GetApprovalsByChangeStepId
     * @request GET: /api/v1/Approvals/GetApprovalsByChangeStepId/{id}
     * @secure
     */
    GetApprovalsByChangeStepId: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<[]>>({
        url: `/Approvals/GetApprovalsByChangeStepId/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Approve or reject a change
     *
     * @tags Approvals
     * @name ApproveChange
     * @request POST:api/v1/Approvals/ApproveChange
     * @secure
     */
    ApproveChange: (
      data: IApproveChangeRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<[]>>({
        url: `/Approvals/ApproveChange`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Send approval notification with approval ids and change step id
     *
     * @tags Approvals
     * @name SendApprovalNotification
     * @request POST: /api/v1/Approvals/SendApprovalNotification
     * @secure
     */
    SendApprovalNotification: (
      data: ISendApprovalNotification,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<[]>>({
        url: `/Approvals/SendApprovalNotification`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Add approval members to a change step
     *
     * @tags Approvals
     * @name AddApprovalMembers
     * @request POST: /api/v1/Approvals/AddApprovalMembers
     * @secure
     */
    AddApprovalMembers: (
      data: IAddApprovalMembers,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<[]>>({
        url: `/Approvals/AddApprovalMembers`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Remove an approval member from a change step
     *
     * @tags Approvals
     * @name RemoveApprovalMembers
     * @request DELETE: /api/v1/Approvals/RemoveApprovalMembers
     * @secure
     */
    RemoveApprovalMembers: (
      data: IAddApprovalMembers,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<[]>>({
        url: `/Approvals/RemoveApprovalMembers`,
        method: 'DELETE',
        data,
        ...config
      }),
    /**
     * Obtiene el estado de aprobación para mostrar el modal
     *
     * @tags Approvals
     * @name ModalValidation
     * @request GET: /api/v1/Approvals/ModalValidation
     * @secure
     */
    ModalValidation: (
      params: IModalValidationResquest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IModalValidationResponse>>({
        url: `/Approvals/ModalValidation`,
        method: 'GET',
        params,
        ...config
      })
  },
  ChangeFlow: {
    /**
     * Búsqueda general de flujos
     *
     * @tags ChangeFlow
     * @name search
     * @request POST: /api/v1/ChangeFlow/search
     * @secure
     */
    search: (data: IParams, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<IItemFlowResponse>>
      >({
        url: `/ChangeFlow/search`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene un flujo por su Id
     *
     * @tags ChangeFlow
     * @name GetChangeFlow
     * @request GET: /api/v1/ChangeFlow
     * @secure
     */
    getChangeFlow: (
      params: IChangeFlowParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IFlowDiagramResponse>>({
        url: `/ChangeFlow`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Crea un nuevo flujo de cambio
     *
     * @tags ChangeFlow
     * @name create
     * @request POST:/api/v1/ChangeFlow
     * @secure
     */
    create: (data: IFlowOfChangeRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ItemCABResponse>>({
        url: `/ChangeFlow`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Actualiza un flujo de cambio.
     *
     * @tags ChangeFlow
     * @name update
     * @request PUT: /api/v1/ChangeFlow
     * @secure
     */
    update: (data: IFlowOfChangeRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ItemCABResponse>>({
        url: `/ChangeFlow`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Actualiza un flujo de cambio.
     *
     * @tags ChangeFlow
     * @name delete
     * @request delete: /api/v1/ChangeFlow/{id}
     * @secure
     */
    delete: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ItemCABResponse>>({
        url: `/ChangeFlow/${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Obtiene las etapas de un flujo por su Id o el base si no se envía
     *
     * @tags ChangeFlow
     * @name getStagesOfChangeFlow
     * @request GET:/api/v1/ChangeFlow/Stages
     * @secure
     */
    getStagesOfChangeFlow: (
      params: { id?: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IItemFlowById>>({
        url: `/ChangeFlow/stages`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Obtiene listado de los estados disponibles para cambiar por ChangeId
     *
     * @tags ChangeFlow
     * @name getStatusesAvailableByChangeId
     * @request GET:/api/v1/ChangeFlow/31/AvailableStages
     * @secure
     */
    getStatusesAvailableByChangeId: (
      params: { id?: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ChangeFlowAvailableStages[]>>(
        {
          url: `/ChangeFlow/${params.id}/AvailableStages`,
          method: 'GET',
          ...config
        }
      )
  },
  tasks: {
    /**
     * Get Task by Ticket Id
     *
     * @tags TASKS
     * @name generalSearchTasks
     * @request GET:/api/v1/Task/SearchTasksByTicketId
     * @secure
     */
    generalSearchTasks: (
      params: SearchTasksParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<TaskResponse>>>({
        url: `/Task/SearchTasksByTicketId`,
        method: 'GET',
        params,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat' }),
        ...config
      }),
    /**
     * Create new Task
     *
     * @tags TASKS
     * @name CreateNewTicketTask
     * @request POST:/api/v1/Task/CreateTaskTicket
     * @secure
     */
    CreateNewTicketTask: (
      data: CreationTask | CreatenewTaskTemplate,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Task/CreateTaskTicket`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Get Task by Id
     *
     * @tags TASKS
     * @name getTaskById
     * @request GET:/api/v1/Task/{id}
     * @secure
     */
    getTaskById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<EditTaskResponse>>({
        url: `/Task/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Delete Task by Id
     *
     * @tags TASKS
     * @name deleteTaskById
     * @request DELETE:/api/v1/Task/DeleteTask/{id}
     * @secure
     */
    deleteTaskById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Task/DeleteTask?taskId=${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Update Task
     *
     * @tags TASKS
     * @name updateTask
     * @request PUT:/api/v1/Task/{id}
     * @secure
     */
    updateTask: (data: EditTaskRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<EditTaskResponse>>({
        url: `/Task`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Realiza una búsqueda tareas de un problema
     *
     * @tags Task
     * @name SearchTasksByProblemId
     * @request GET:/api/v1/Task/SearchTasksByProblemId
     * @secure
     */
    SearchTasksByProblemId: (
      params: SearchTasksParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<TaskResponse>>>({
        url: `/Task/SearchTasksByProblemId`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Consulta la cantidad de tareas completas de un problema
     *
     * @tags Task
     * @name GetTaskCompleteByProblemId
     * @request GET:/api/v1/Task/GetTaskCompleteByProblem/{id}
     * @secure
     */
    GetTaskCompletedByProblemId: (
      id: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ITaskByProblemId>>({
        url: `/Task/GetTaskCompleteByProblem/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Crea una tarea para un Problema
     *
     * @tags Task
     * @name CreateTaskProblem
     * @request POST:/api/v1/Task/CreateTaskProblem
     * @secure
     */
    CreateTaskProblem: (data: CreationTask, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Task/CreateTaskProblem`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene el orden de las tareas de un problema
     *
     * @tags Task
     * @name GetTaskOrderProblem
     * @request GET:/api/v1/Task/GetOrderTaskByProblemId
     * @secure
     */
    GetTaskOrderProblem: (problemId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<TaskOrderProblem[]>>({
        url: `/Task/GetOrderTaskByProblemId?ProblemId=${problemId}`,
        method: 'GET',
        ...config
      }),
    /**
     * Actualiza el orden de las tareas de un problema
     *
     * @tags Task
     * @name UpdateTaskOrderProblem
     * @request PUT:/api/v1/Task/GetOrderTaskByProblemId
     * @secure
     */
    UpdateTaskOrderProblem: (
      data: NewTaskOrderProblem[],
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Task/UpdateOrderTask`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Crea las tareas de una plantilla de tarea
     *
     * @tags Task
     * @name CreateTaskByTemplate
     * @request POST:/api/v1/Task/CreateTasksFromTemplates
     * @secure
     */
    CreateTaskByTemplate: (
      data: CreateTaskByTemplate,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Task/CreateTasksFromTemplates`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Realiza una búsqueda tareas de un cambio
     *
     * @tags Task
     * @name SearchTasksByChangeId
     * @request GET:/api/v1/Changes/GetTasks?ChangeId=1&ChangeStageId=1
     * @secure
     */
    SearchTasksByChangeId: (
      params: SearchTasksParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TaskResponseInChange>>({
        url: `/Changes/GetTasks`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Consulta la cantidad de tareas completas de un cambio
     *
     * @tags Task
     * @name GetTaskCompleteByChangeId
     * @request GET:/api/v1/Task/GetTaskCompleteByChange/{id}
     * @secure
     */
    GetTaskCompletedByChangeId: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ITaskByChangeId>>({
        url: `/Task/GetTaskCompleteByChange/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Crea una tarea para un Cambio
     *
     * @tags Task
     * @name CreateTaskChange
     * @request POST:/api/v1/Task/CreateTaskChange
     * @secure
     */
    CreateTaskChange: (data: CreationTask, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Task/CreateTaskChange`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene el orden de las tareas de un cambio
     *
     * @tags Task
     * @name GetTaskOrderChange
     * @request GET:/api/v1/Task/GetOrderTaskByChangeId
     * @secure
     */
    GetTaskOrderChange: (changeId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<TaskOrderChange[]>>({
        url: `/Task/GetOrderTaskByChangeId?ChangeId=${changeId}`,
        method: 'GET',
        ...config
      }),
    /**
     * Actualiza el orden de las tareas de un cambio
     *
     * @tags Task
     * @name UpdateTaskOrderChange
     * @request PUT:/api/v1/Task/UpdateOrderTaskChange
     * @secure
     */
    UpdateTaskOrderChange: (
      data: NewTaskOrderChange[],
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Task/UpdateOrderTask`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Crea una tarea para un Release
     *
     * @tags Task
     * @name CreateTaskRelease
     * @request POST:/api/v1/Task/CreateTaskRelease
     * @secure
     */
    CreateTaskRelease: (
      data: CreateTaskRelease,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Task/CreateTaskRelease`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Realiza una búsqueda tareas de un release
     *
     * @tags Task
     * @name SearchTasksByReleaseId
     * @request GET:/api/v1/Task/GetTasksByReleaseId?ReleaseId=123
     * @secure
     */
    SearchTasksByReleaseId: (
      releaseId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<CreateTaskRelease>>({
        url: `/Task/GetTasksByReleaseId?ReleaseId=${releaseId}`,
        method: 'GET',
        ...config
      })
  },
  changes: {
    /**
     * Get Task by Ticket Id
     *
     * @tags TASKS
     * @name generalSearchTasks
     * @request GET:/api/v1/Task/SearchTasksByTicketId
     * @secure
     */
    getByChange: (changeId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IChangeResponse>>({
        url: `/Changes/${changeId}`,
        method: 'GET',
        ...config
      }),
    /**
     * Búsqueda de cambios
     *
     * @tags Changes
     * @name searchChange
     * @request POST: /api/v1/Changes/search
     * @secure
     */
    searchChange: (data: IParamsChange, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<PagedList<IChangeResponse>>>({
        url: `/Changes/search`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Verifica duplicados antes de crear un cambio.
     *
     * @tags Changes
     * @name checkDuplicates
     * @request POST: /api/v1/Changes/CheckDuplicates
     * @secure
     */
    checkDuplicates: (
      data: SaveTicketRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ICheckDuplicatesResponse>>({
        url: `/Changes/CheckDuplicates`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Crea un cambio.
     *
     * @tags CreateNewChange
     * @name CreateNewChange
     * @request POST: /api/v1/Changes
     * @secure
     */
    create: (data: SaveTicketRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Editar un cambio.
     *
     * @tags updateChange
     * @name updateChange
     * @request PUT: /api/v1/Changes
     * @secure
     */
    update: (data: IParamsChange, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Elimina un comité por su ID.
     *
     * @tags DeleteChange
     * @name DeleteChange
     * @request DELETE: /api/v1/ChangeAdvisoryBoard/{id}
     * @secure
     */
    delete: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ItemCABResponse>>({
        url: `/Changes/${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Agrega un comentario al cambio actual
     *
     * @tags Changes
     * @name addComments
     * @request POST: /api/v1/Changes/{id}/comments
     * @secure
     */
    addComments: (data: ICommentChange, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/${data?.changeId}/comments`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Agrega uno o mas productos a un cambio
     *
     * @tags Changes
     * @name addProducts
     * @request POST: /api/v1/Changes/{id}/products
     * @secure
     */
    addProducts: (data: IProductChange, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/${data?.changeId}/products`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Elimina un producto del cambio
     *
     * @tags Changes
     * @name deleteProduct
     * @request PUT: /api/v1/Changes/UpdateChangeStage
     * @secure
     */
    updateChangeStage: (
      data: UpdateChangeStageRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/UpdateChangeStage`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Actualiza el estatus de un cambio
     *
     * @tags Changes
     * @name updateChangeStatus
     * @request POST: /api/v1/Changes/{id}/products
     * @secure
     */
    deleteProduct: (data: IProductChange, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/${data?.changeId}/products`,
        method: 'DELETE',
        data,
        ...config
      }),
    /**
     * Agrega uno o mas productos afectados a un cambio
     *
     * @tags Changes
     * @name getAffectedProducts
     * @request GET: /api/v1/Changes/{id}/affectedProducts
     * @secure
     */
    getAffectedProducts: (changeId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<AffectedAndLinkedProducts>>({
        url: `/Changes/${changeId}/affectedProducts`,
        method: 'GET',
        ...config
      }),
    /**
     * Agrega uno o mas productos afectados a un cambio
     *
     * @tags Changes
     * @name addAffectedProducts
     * @request POST: /api/v1/Changes/{id}/affectedProducts
     * @secure
     */
    addAffectedProducts: (
      data: IProductChange,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/${data?.changeId}/affectedProducts`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Elimina un producto afectado del cambio
     *
     * @tags Changes
     * @name deleteAffectedProduct
     * @request DELETE: /api/v1/Changes/{id}/affectedProducts
     * @secure
     */
    deleteAffectedProduct: (
      data: IProductChange,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/${data?.changeId}/affectedProducts`,
        method: 'DELETE',
        data,
        ...config
      }),
    /**
     * Envia los datos de planificación de un cambio
     *
     * @tags Changes
     * @name sendPlanificationData
     * @request PUT: /api/v1/Changes/Plan
     * @secure
     */
    sendPlanificationData: (
      data: ISendPlanificationData,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/Plan`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Envia los datos de cierre de un cambio
     *
     * @tags Changes
     * @name sendClosureData
     * @request PUT: /api/v1/Changes/UpdateCloseInformation
     * @secure
     */
    sendClosureData: (
      data: ISendClosureData,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/UpdateCloseInformation`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Envia los datos de revisión de un cambio
     *
     * @tags Changes
     * @name sendReviewData
     * @request PUT: /api/v1/Changes/UpdateReviewInformation

     * @secure
     */
    sendReviewData: (data: ISendReviewData, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/UpdateReviewInformation`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Actualiza el status de un cambio
     *
     * @tags Changes
     * @name UpdateChangeStatus
     * @request PUT: /api/v1/Changes/UpdateChangeStatus
     * @secure
     */
    UpdateChangeStatus: (
      data: IUpdateChangeStatus,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/UpdateChangeStatus`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Obtiene los problemas asociados a un cambio con paginación
     *
     * @tags Changes
     * @name getRelatedProblems
     * @request POST: /api/v1/Changes/GetProblemsByChange
     * @secure
     */
    getRelatedProblems: (
      params: GetProblemsByChangeRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<IProblemSearchResponse>>
      >({
        url: '/Changes/GetProblemsByChange',
        method: 'POST',
        data: params,
        ...config
      }),
    /**
     * Obtiene los tickets asociados a un cambio con paginación
     *
     * @tags Changes
     * @name getRelatedTickets
     * @request POST: /api/v1/Changes/GetTicketsByChange
     * @secure
     */
    getRelatedTickets: (
      params: GetTicketsByChangeRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<ITicketProblemResponse>>
      >({
        url: '/Changes/GetTicketsByChange',
        method: 'POST',
        data: params,
        ...config
      }),
    /**
     * Establece un problema como relacionado al cambio
     *
     * @tags Changes
     * @name SetRelatedProblem
     * @request POST: /api/v1/Changes/{id}/problems
     * @secure
     */
    SetRelatedProblem: (
      id: number,
      data: IRelatedProblemChange,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Changes/${id}/problems`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Elimina la relación entre un problema y un cambio
     *
     * @tags Changes
     * @name RemoveRelatedProblem
     * @request DELETE: /api/v1/Changes/{id}/problems
     * @secure
     */
    RemoveRelatedProblem: (
      id: number,
      data: IRelatedProblemChange,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Changes/${id}/problems`,
        method: 'DELETE',
        data,
        ...config
      }),
    /**
     * Establece un ticket como relacionado al cambio
     *
     * @tags Changes
     * @name SetRelatedTicket
     * @request POST: /api/v1/Changes/{id}/tickets
     * @secure
     */
    SetRelatedTicket: (
      id: number,
      data: IRelatedTicketChange,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Changes/${id}/tickets`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Elimina la relación entre un ticket y un cambio
     *
     * @tags Changes
     * @name RemoveRelatedTicket
     * @request DELETE: /api/v1/Changes/{id}/tickets
     * @secure
     */
    RemoveRelatedTicket: (
      id: number,
      data: IRelatedTicketChange,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Changes/${id}/tickets`,
        method: 'DELETE',
        data,
        ...config
      }),
    /**
     * Obtiene los incidentes disponibles de un cliente que no están asociados a ningún cambio
     *
     * @tags Changes
     * @name GetAvailableIncidentsByClient
     * @request POST: /api/v1/Changes/GetAvailableIncidentsByClient
     * @secure
     */
    GetAvailableIncidentsByClient: (
      data: IParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<ITicketProblemResponse>>
      >({
        url: '/Changes/GetAvailableIncidentsByClient',
        method: 'POST',
        data,
        ...config
      }),
    /**
     * search release by changeId
     *
     * @tags Changes
     * @name searchReleaseByChangeId
     * @request GET: /api/v1/Changes/123/releases
     * @secure
     */
    searchReleaseByChangeId: (
      changeId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<SearchReleaseByChangeIdResponse[]>
      >({
        url: `/Changes/${changeId}/releases`,
        method: 'GET',
        ...config
      }),
    /**
     * get changes by ticket
     *
     * @tags Changes
     * @name getChangesByTicket
     * @request POST: /api/v1/Changes/GetChangesByTicket
     * @secure
     */
    getChangesByTicket: (
      data: IParams & { ticketId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<IChangeResponse>>>({
        url: `/Changes/GetChangesByTicket`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * create a change from a ticket
     *
     * @tags Changes
     * @name createChangeFromTicket
     * @request POST: /api/v1/Changes/CreateChangeFromTicket
     * @secure
     */
    createChangeFromTicket: (
      data: SaveTicketRequest & { ticketId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IChangeResponse>>({
        url: `/Changes/CreateChangeFromTicket`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * associate a change to a ticket
     *
     * @tags Changes
     * @name associateChangeToTicket
     * @request POST: /api/v1/Changes/Tickets/123/changes
     * @secure
     */
    associateChangeToTicket: (
      data: {
        ticketId: number;
        changeIds: number[];
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/Tickets/${data?.ticketId}/changes`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * disassociate a change to a ticket
     *
     * @tags Changes
     * @name disassociateChangeToTicket
     * @request DELETE: /api/v1/Changes/Tickets/123/changes
     * @secure
     */
    disassociateChangeToTicket: (
      data: {
        ticketId: number;
        changeIds: number[];
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Changes/Tickets/${data?.ticketId}/changes`,
        method: 'DELETE',
        data,
        ...config
      }),
    /**
     * get available problems by client
     *
     * @tags Changes
     * @name getAvailableProblemsByClient
     * @request POST: /api/v1/Changes/GetAvailableProblemsByClient
     * @secure
     */
    getAvailableProblemsByClient: (
      data: IParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<IProblemSearchResponse[]>>
      >({
        url: `/Changes/GetAvailableProblemsByClient`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * get available changes by client
     *
     * @tags Changes
     * @name getAvailableChangesByClient
     * @request POST: /api/v1/Changes/GetAvailableChangesByClient
     * @secure
     */
    getAvailableChangesByClient: (
      data: IParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<IProblemSearchResponse[]>>
      >({
        url: `/Changes/GetAvailableChangesByClient`,
        method: 'POST',
        data,
        ...config
      })
  },
  ChangeStatus: {
    /**
     * Obtiene los estados disponibles para un cambio
     *
     * @tags ChangeStatus
     * @name getChangeStatusAvailable
     * @request GET: /api/v1/Changes/{changeId}/AvailableStatuses
     * @secure
     */
    getChangeStatusAvailable: (
      changeId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IChangeStatusAvailable[]>>({
        url: `/Changes/${changeId}/AvailableStatuses`,
        method: 'GET',
        ...config
      })
  },
  PIR: {
    /**
     * Obtiene todos los resultados de un PIR
     *
     * @tags Changes
     * @name getPIRResults
     * @request GET: process/api/v1/Pir/Results?onlyEnabled=true
     * @secure
     */
    getPIRResults: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IPIRStatusResponse[]>>({
        url: `/Pir/Results?onlyEnabled=true`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene todos los estados de un PIR
     *
     * @tags Changes
     * @name getPIRStatuses
     * @request GET: process/api/v1/Pir/Statuses?onlyEnabled=true
     * @secure
     */
    getPIRStatuses: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IPIRStatusResponse[]>>({
        url: `/Pir/Statuses?onlyEnabled=true`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene todos los PIRs de un cambio
     *
     * @tags Changes
     * @name getPIRsByChange
     * @request GET: /process/api/v1/Pir/{changeId}
     * @secure
     */
    getPIRsByChange: (changeId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IPIRResponse>>({
        url: `/Pir/${changeId}`,
        method: 'GET',
        ...config
      }),
    /**
     * Crea un nuevo PIR
     *
     * @tags Changes
     * @name createPIR
     * @request POST: /api/v1/PIR
     * @secure
     */
    createPIR: (data: ICreatePIRRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IPIRResponse>>({
        url: `/PIR`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Actualiza un PIR existente
     *
     * @tags Changes
     * @name updatePIR
     * @request PUT: /process/api/v1/Pir
     * @secure
     */
    updatePIR: (data: IUpdatePIRRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IPIRResponse>>({
        url: `/Pir`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Elimina un PIR
     *
     * @tags Changes
     * @name deletePIR
     * @request DELETE: process/api/v1/Pir?Id=1&ChangeId=2
     * @secure
     */
    deletePIR: (
      changeId: number,
      pirId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Pir?Id=${pirId}&ChangeId=${changeId}`,
        method: 'DELETE',
        ...config
      })
  },
  solutions: {
    /**
     * GeneralSearch Solutions
     *
     * @tags SOLUTIONS
     * @name searchSolutions
     * @request POST:/api/v1/Solution/search
     * @secure
     */
    searchSolutions: (
      data: SearchSolutionsRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<SearchSolutionsResponse>>
      >({
        url: `/Solution/search`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Get all solutions Status
     *
     * @tags SOLUTIONS
     * @name getAllSolutionsStatus
     * @request GET:/api/v1/Solution/GetAllSolutionStates
     * @secure
     */
    getAllSolutionsStatus: (config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<GetAllSolutionStatesResponse[]>
      >({
        url: `/Solution/GetAllSolutionStates`,
        method: 'GET',
        ...config
      }),
    /**
     * Create new Solution
     *
     * @tags SOLUTIONS
     * @name createNewSolution
     * @request POST:/api/v1/Solution
     * @secure
     */
    createNewSolution: (
      data: NewSolutionRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
          createdDateTimeUTC?: Date;
        }>
      >({
        url: `/Solution`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * update Solution
     *
     * @tags SOLUTIONS
     * @name updateSolution
     * @request PUT:/api/v1/Solution
     * @secure
     */
    updateSolution: (
      data: SolutionUpdateRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
          createdDateTimeUTC?: Date;
        }>
      >({
        url: `/Solution`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Get Solution By Id
     *
     * @tags SOLUTIONS
     * @name getSolutionById
     * @request GET:/api/v1/Solution/1
     * @secure
     */
    getSolutionById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SolutionByIDResponse>>({
        url: `/Solution/${id}`,
        method: 'GET',
        ...config
      }),

    /**
     * Asociar una solución a problema/s
     *
     * @tags SOLUTIONS
     * @name RelatedProblem
     * @request   POST: /api/v1/Solution/RelatedProblem
     * @secure
     */
    RelatedProblem: (data: IRelatedProblem, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
          createdDateTimeUTC?: Date;
        }>
      >({
        url: `/Solution/RelatedProblem`,
        method: 'POST',
        data,
        ...config
      })
  },
  problems: {
    /**
     * GeneralSearch Problems
     *
     * @tags Problems
     * @name search
     * @request POST:/api/v1/Problems/search
     * @secure
     */
    search: (data: IProblemSearchRequest, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<IProblemSearchResponse>>
      >({
        url: `/Problems/search`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Crea un nuevo Problema
     *
     * @tags Problems
     * @name create
     * @request POST:/api/v1/Problems
     * @secure
     */
    create: (data: ICreateProblem, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Problems`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Actualiza un Problema
     *
     * @tags Problems
     * @name update
     * @request POST:/api/v1/Problems
     * @secure
     */
    update: (data: ICreateProblem, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<{ id: number }>>({
        url: `/Problems`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Elimina un Problema
     *
     * @tags Problems
     * @name delete
     * @request POST:/api/v1/Problems/{id}
     * @secure
     */
    delete: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<{ id: number }>>({
        url: `/Problems/${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Archiva o desarchiva un Problema (SAIMONOPE-898)
     *
     * @tags Problems
     * @name archive
     * @request POST:/api/v1/Problems/Archive
     * @secure
     */
    archive: (
      data: IArchiveProblemRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<{ id: number }>>({
        url: `/Problems/Archive`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene un Problema por id
     *
     * @tags Problems
     * @name getById
     * @request POST:/api/v1/Problems/{id}
     * @secure
     */
    getById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IProblemDetail>>({
        url: `/Problems/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Historial de cambios (auditoría) de un problema.
     *
     * @tags Problems
     * @name getTrackingList
     * @request POST:/api/v1/Problems/ProblemTrackingList
     * @secure
     */
    getTrackingList: (
      data: IProblemTrackingRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<IProblemTrackingResponse>>
      >({
        url: `/Problems/ProblemTrackingList`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Crea un nuevo Problema
     *
     * @tags Problems
     * @name SetProblemKnownIssue
     * @request POST:/api/v1/Problems/SetProblemKnownIssue
     * @secure
     */
    SetProblemKnownIssue: (
      data: ISetProblemKnownIssue,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Problems/SetProblemKnownIssue`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Actualiza el estatus de un problema
     *
     * @tags Problems
     * @name UpdateProblemStatus
     * @request POST:/api/v1/Problems/UpdateProblemStatus
     * @secure
     */
    UpdateProblemStatus: (
      data: IUpdateProblemStatus,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Problems/UpdateProblemStatus`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Asignar un problema
     *
     * @tags Problems
     * @name AssignProblem
     * @request POST:/api/v1/Problems/AssignProblem
     * @secure
     */
    AssignProblem: (data: IAssignProblem, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Problems/AssignProblem`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Asignar una prioridad a un cambio
     *
     * @tags Problems
     * @name UpdateProblemPriority
     * @request POST:/api/v1/Problems/UpdateProblemPriority
     * @secure
     */
    UpdateProblemPriority: (
      data: IUpdateProblemPriority,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Problems/UpdateProblemPriority`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Cambia la etapa de un problema
     *
     * @tags Problems
     * @name changeStage
     * @request POST:/api/v1/Problems/stage
     * @secure
     */
    changeStage: (
      data: IChangeProblemStageRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<void>>({
        url: `/Problems/stage`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Agrega uno o varios productos al Problema
     *
     * @tags Problems
     * @name addProducts
     * @request POST:/api/v1/Problems/Problems/{id}/products
     * @secure
     */
    addProducts: (
      problemId: number,
      data: { productIds: number[] },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Problems/${problemId}/products`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Elimina uno o varios productos del Problema
     *
     * @tags Problems
     * @name deleteProducts
     * @request POST:/api/v1/Problems/Problems/{id}/products
     * @secure
     */
    deleteProducts: (
      problemId: number,
      data: { productIds: number[] },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Problems/${problemId}/products`,
        method: 'DELETE',
        data,
        ...config
      }),
    /**
     * Obtiene las Soluciones asociadas a un Problema
     *
     * @tags Problems
     * @name getSolutionsRelated
     * @request GET: /api/v1/Problems/{id}/solutions
     * @secure
     */
    getRelatedSolutions: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SearchSolutionsResponse[]>>({
        url: `/Problems/${id}/solutions`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene las solicitudes vinculadas a un problema
     *
     * @tags Problems
     * @name getRelatedRequests
     * @request GET:/api/v1/Problems/{id}/RelatedTickets
     * @secure
     */
    getRelatedRequests: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ITicketProblemResponse[]>>({
        url: `/Problems/${id}/RelatedTickets`,
        method: 'GET',
        ...config
      }),
    /**
     * Guarda la configuración del comité del problema (etapa Enjambre)
     *
     * @tags Problems
     * @name saveCommittee
     * @request PUT:/api/v1/Problems/{id}/committee
     * @secure
     */
    saveCommittee: (
      problemId: number,
      data: ISaveProblemCommitteeRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ problemId: number; totalAssignments: number }>
      >({
        url: `/Problems/${problemId}/committee`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Quita una asignación del comité del problema
     *
     * @tags Problems
     * @name removeCommitteeAssignment
     * @request DELETE:/api/v1/Problems/{id}/committee/{assignmentId}
     * @secure
     */
    removeCommitteeAssignment: (
      problemId: number,
      assignmentId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Problems/${problemId}/committee/${assignmentId}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Verifica si puede avanzar desde la etapa Comité
     *
     * @tags Problems
     * @name getCanAdvanceFromCommittee
     * @request GET:/api/v1/Problems/{id}/committee/can-advance
     * @secure
     */
    getCanAdvanceFromCommittee: (
      problemId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<IGetCanAdvanceFromComiteStageResponse>
      >({
        url: `/Problems/${problemId}/committee/can-advance`,
        method: 'GET',
        ...config
      }),
    /**
     * Establece un ticket de tipo "Solicitud" como relacionado al problema
     *
     * @tags Problems
     * @name SetRelatedTicket
     * @request   POST:/api/v1/Problems/SetRelatedTicket
     * @secure
     */
    SetRelatedTicket: (
      data: IRelatedTicketProblem,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Problems/SetRelatedTicket`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Elimina la relación entre un ticket y un problema
     *
     * @tags Problems
     * @name RemoveRelatedTicket
     * @request DELETE:/api/v1/Problems/RemoveRelatedTicket
     * @secure
     */
    RemoveRelatedTicket: (
      data: IRelatedTicketProblem,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Problems/RemoveRelatedTicket`,
        method: 'DELETE',
        data,
        ...config
      }),
    /**
     * Obtiene las secciones de análisis de problemas
     *
     * @tags Problems
     * @name getAnalysisSections
     * @request GET:/api/v1/Problems/AnalysisSections
     * @secure
     */
    getAnalysisSections: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IAnalysisSection[]>>({
        url: `/Problems/AnalysisSections`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene las notas asociadas a un Problema
     *
     * @tags Problems
     * @name getNotes
     * @request GET: /api/v1/Problems/{id}/note
     * @secure
     */
    getNotes: (
      problemId: number,
      params: { analysisSectionId?: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IAnalysisSectionNotes[]>>({
        url: `/Problems/${problemId}/note`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     *  Guardar una Nota de un Problema
     *
     * @tags Problems
     * @name CreateProblemNote
     * @request   POST: /api/v1/Problems/CreateProblemNote
     * @secure
     */
    CreateProblemNote: (
      data: ICreateProblemNote,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Problems/CreateProblemNote`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     *  Actualiza una Nota de un Problema
     *
     * @tags Problems
     * @name UpdateNote
     * @request PUT: /api/v1/Problems/UpdateNote
     * @secure
     */
    UpdateNote: (data: IUpdateProblemNote, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          noteId?: number;
        }>
      >({
        url: `/Problems/UpdateNote`,
        method: 'PUT',
        data,
        ...config
      }),

    /**
     * Eliminar una Nota de un problema
     *
     * @tags Problems
     * @name DeleteProblemNote
     * @request DELETE: /api/v1/Problems/DeleteProblemNote
     * @secure
     */
    DeleteProblemNote: (
      params: { noteId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Problems/DeleteProblemNote`,
        method: 'DELETE',
        params,
        ...config
      }),

    /**
     * Obtener el progreso y estado actual de las etapas de un problema
     *
     * @tags Problems
     * @name GetProgress
     * @request GET: /api/v1/Problems/{problemId}/progress
     * @secure
     */
    getProgress: (problemId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IGetProblemProgressResponse>>(
        {
          url: `/Problems/${problemId}/progress`,
          method: 'GET',
          ...config
        }
      ),

    /**
     * Obtener el análisis de los 5 Por Qué de un problema
     *
     * @tags Problems
     * @name GetFiveWhys
     * @request GET: /api/v1/Problems/{problemId}/five-whys
     * @secure
     */
    getFiveWhys: (problemId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IGetFiveWhysResponse>>({
        url: `/Problems/${problemId}/five-whys`,
        method: 'GET',
        ...config
      }),

    /**
     * Guardar el análisis de los 5 Por Qué de un problema
     *
     * @tags Problems
     * @name SaveFiveWhys
     * @request PUT: /api/v1/Problems/{problemId}/five-whys
     * @secure
     */
    saveFiveWhys: (
      problemId: number,
      data: ISaveFiveWhysRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ISaveFiveWhysResponse>>({
        url: `/Problems/${problemId}/five-whys`,
        method: 'PUT',
        data,
        ...config
      }),

    /**
     * Obtener las categorías disponibles para el diagrama de Ishikawa
     *
     * @tags Problems
     * @name GetIshikawaCategories
     * @request GET: /api/v1/Problems/control-stage/ishikawa-categories
     * @secure
     */
    getIshikawaCategories: (config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<IGetIshikawaCategoriesResponse>
      >({
        url: `/Problems/control-stage/ishikawa-categories`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtener el diagrama de Ishikawa de un problema
     *
     * @tags Problems
     * @name GetIshikawa
     * @request GET: /api/v1/Problems/{problemId}/ishikawa
     * @secure
     */
    getIshikawa: (problemId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IGetIshikawaResponse>>({
        url: `/Problems/${problemId}/ishikawa`,
        method: 'GET',
        ...config
      }),

    /**
     * Guardar el diagrama de Ishikawa de un problema
     *
     * @tags Problems
     * @name SaveIshikawa
     * @request PUT: /api/v1/Problems/{problemId}/ishikawa
     * @secure
     */
    saveIshikawa: (
      problemId: number,
      data: ISaveIshikawaRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ISaveIshikawaResponse>>({
        url: `/Problems/${problemId}/ishikawa`,
        method: 'PUT',
        data,
        ...config
      }),

    /**
     * Obtener las preguntas de la encuesta de categoría de un problema
     *
     * @tags Problems
     * @name GetCategorySurveyQuestions
     * @request GET: /api/v1/Problems/{problemId}/control-stage/category-survey-questions
     * @secure
     */
    getCategorySurveyQuestions: (
      problemId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<IGetProblemCategorySurveyQuestionsResponse>
      >({
        url: `/Problems/${problemId}/control-stage/category-survey-questions`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtener las respuestas de la encuesta de categoría de un problema
     *
     * @tags Problems
     * @name GetCategorySurveyAnswers
     * @request GET: /api/v1/Problems/{problemId}/control-stage/category-survey-answers
     * @secure
     */
    getCategorySurveyAnswers: (
      problemId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<IGetProblemCategorySurveyAnswersResponse>
      >({
        url: `/Problems/${problemId}/control-stage/category-survey-answers`,
        method: 'GET',
        ...config
      }),

    /**
     * Guardar las respuestas de la encuesta de categoría de un problema
     *
     * @tags Problems
     * @name SaveCategorySurveyAnswers
     * @request PUT: /api/v1/Problems/{problemId}/control-stage/category-survey-answers
     * @secure
     */
    saveCategorySurveyAnswers: (
      problemId: number,
      data: ISaveProblemCategorySurveyAnswersRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ problemId: number; savedCount?: number }>
      >({
        url: `/Problems/${problemId}/control-stage/category-survey-answers`,
        method: 'PUT',
        data,
        ...config
      }),

    /**
     * Guardar toda la información de la etapa de Control de un problema
     *
     * @tags Problems
     * @name SaveControlStage
     * @request PUT: /api/v1/Problems/{problemId}/control-stage
     * @secure
     */
    saveControlStage: (
      problemId: number,
      data: ISaveControlStageRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ISaveControlStageResponse>>({
        url: `/Problems/${problemId}/control-stage`,
        method: 'PUT',
        data,
        ...config
      }),

    /**
     * Verificar si un problema puede avanzar desde la etapa de Control
     *
     * @tags Problems
     * @name GetCanAdvanceFromControl
     * @request GET: /api/v1/Problems/{problemId}/can-advance-from-control
     * @secure
     */
    getCanAdvanceFromControl: (
      problemId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<IGetCanAdvanceFromControlResponse>
      >({
        url: `/Problems/${problemId}/can-advance-from-control`,
        method: 'GET',
        ...config
      }),

    /**
     * Asociar un ticket de workaround a un problema
     *
     * @tags Problems
     * @name AssociateWorkaroundTicket
     * @request POST: /api/v1/Problems/workaround-tickets
     * @secure
     */
    associateWorkaroundTicket: (
      data: IAssociateWorkaroundTicketRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<IAssociateWorkaroundTicketResponse>
      >({
        url: `/Problems/workaround-tickets`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Obtener los consejos y tips para la etapa de Control
     *
     * @tags Problems
     * @name GetControlStageTips
     * @request GET: /api/v1/Problems/control-stage/tips
     * @secure
     */
    getControlStageTips: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IControlStageTipsResponse>>({
        url: `/Problems/control-stage/tips`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtener los datos de exportación de la etapa de Control de un problema
     *
     * @tags Problems
     * @name GetControlStageExportData
     * @request GET: /api/v1/Problems/{problemId}/control-stage/export-data
     * @secure
     */
    getControlStageExportData: (
      problemId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IControlStageExportData>>({
        url: `/Problems/${problemId}/control-stage/export-data`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtiene los tickets relacionados con el problema (incluye workarounds)
     *
     * @name GetRelatedTickets
     * @request GET: /api/v1/Problems/{id}/related-tickets
     * @secure
     */
    getRelatedTickets: (problemId: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IWorkaroundHistoryItem[]>>({
        url: `/Problems/${problemId}/RelatedTickets`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtiene preview antes de finalizar el problema
     *
     * @name GetFinalizePreview
     * @request GET: /api/v1/Problems/{id}/finalize-preview
     * @secure
     */
    getFinalizePreview: (problemId: number, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<IGetFinalizeProblemPreviewResponse>
      >({
        url: `/Problems/${problemId}/finalize-preview`,
        method: 'GET',
        ...config
      }),

    /**
     * Finaliza el problema en Etapa 4
     *
     * @name FinalizeProblem
     * @request POST: /api/v1/Problems/{id}/finalize
     * @secure
     */
    finalizeProblem: (
      problemId: number,
      data: IFinalizeProblemRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<{ message: string }>>({
        url: `/Problems/${problemId}/finalize`,
        method: 'POST',
        data,
        ...config
      })
  },
  problemExpertBoard: {
    /**
     * Lista todos los comités de expertos activos
     *
     * @tags ProblemExpertBoard
     * @name getAll
     * @request GET:/api/v1/ProblemExpertBoard
     * @secure
     */
    getAll: (config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<IProblemExpertBoardResponse[]>
      >({
        url: `/ProblemExpertBoard`,
        method: 'GET',
        ...config
      }),
    /**
     * Búsqueda paginada de comités de expertos
     *
     * @tags ProblemExpertBoard
     * @name search
     * @request POST:/api/v1/ProblemExpertBoard/SearchProblemExpertBoard
     * @secure
     */
    search: (
      params: {
        pageNumber?: number;
        pageSize?: number;
        text?: string;
        orderColumn?: string;
        descending?: boolean;
        isExport?: boolean;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<IProblemExpertBoardResponse>>
      >({
        url: `/ProblemExpertBoard/SearchProblemExpertBoard`,
        method: 'POST',
        params,
        paramsSerializer: (prms) =>
          qs.stringify(prms, { arrayFormat: 'repeat' }),
        ...config
      }),
    /**
     * Crea un comité de expertos.
     *
     * @tags ProblemExpertBoard
     * @name create
     * @request POST:/api/v1/ProblemExpertBoard
     * @secure
     */
    create: (
      data: IProblemExpertBoardCreateRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IProblemExpertBoardResponse>>(
        {
          url: `/ProblemExpertBoard`,
          method: 'POST',
          data,
          ...config
        }
      ),
    /**
     * Actualiza un comité de expertos.
     *
     * @tags ProblemExpertBoard
     * @name update
     * @request PUT:/api/v1/ProblemExpertBoard/{id}
     * @secure
     */
    update: (
      data: IProblemExpertBoardUpdateRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IProblemExpertBoardResponse>>(
        {
          url: `/ProblemExpertBoard`,
          method: 'PUT',
          data,
          ...config
        }
      ),
    /**
     * Obtiene un comité de expertos por su ID.
     *
     * @tags ProblemExpertBoard
     * @name getById
     * @request GET:/api/v1/ProblemExpertBoard/{id}
     * @secure
     */
    getById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IProblemExpertBoardResponse>>(
        {
          url: `/ProblemExpertBoard/${id}`,
          method: 'GET',
          ...config
        }
      ),
    /**
     * Elimina un comité de expertos por su ID.
     *
     * @tags ProblemExpertBoard
     * @name delete
     * @request DELETE:/api/v1/ProblemExpertBoard/{id}
     * @secure
     */
    delete: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<void>>({
        url: `/ProblemExpertBoard/${id}`,
        method: 'DELETE',
        ...config
      })
  },
  ticketStatus: {
    /**
     * Obtiene listado de los estados de un Ticket
     *
     * @tags TicketStatus
     * @name getTicketStatus
     * @request GET:/api/v1/TicketStatus
     * @secure
     */
    getTicketStatus: (config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; name: string }[]>
      >({
        url: `/TicketStatus?onlyEnabled=true`, // se agrega ese filtro porque es
        // obligatorio que traiga solo los estados habilitados
        method: 'GET',
        ...config
      })
  },
  ProblemFlow: {
    /**
     * Obtiene listado de los estados de un Problema
     *
     * @tags ProblemFlow
     * @name generalSearchProblemStatusFlow
     * @request POST:/api/v1/ProblemFlow/search
     * @secure
     */
    generalSearchProblemStatusFlow: (
      data: ProblemFlowSearchRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<ProblemFlowSearch>>
      >({
        url: `/ProblemFlow/search`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene listado de los estados de un Problema por ID
     *
     * @tags ProblemFlow
     * @name generalSearchProblemStatusFlowById
     * @request GET:/api/v1/ProblemFlow/${id}
     * @secure
     */
    generalSearchProblemStatusFlowById: (
      id: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ProblemFlowSearchIDResponse>>(
        {
          url: `/ProblemFlow/${id}`,
          method: 'GET',
          ...config
        }
      ),
    /**
     * Crea un nuevo flujo de estatus de Problemas
     *
     * @tags ProblemFlow
     * @name createNewStatus
     * @request POST: api/v1/ProblemFlow
     * @secure
     */
    createNewStatus: (
      data: CreateProblemFlowRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ProblemFlowSearchIDResponse>>(
        {
          url: `/ProblemFlow`,
          method: 'POST',
          data,
          ...config
        }
      ),
    /**
     * Obtener el diagrama de Gantt de un flujo de estatus de Problemas
     *
     * @tags ProblemFlow
     * @name getGanttDiagram
     * @request GET: /api/v1/ProblemFlow/1/gantt-diagram
     * @secure
     */
    getGanttDiagram: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ProblemFlowSearchIDResponse>>(
        {
          url: `/ProblemFlow/${id}/gantt-diagram`,
          method: 'GET',
          ...config
        }
      ),
    /**
     * Elimina un flujo de estatus de Problemas
     *
     * @tags ProblemFlow
     * @name deleteProblemFlow
     * @request DELETE: api/v1/ProblemFlow/{id}
     * @secure
     */
    deleteProblemFlow: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/ProblemFlow/${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Actualiza un flujo de estatus de Problemas
     *
     * @tags ProblemFlow
     * @name updateProblemFlow
     * @request PUT: api/v1/ProblemFlow
     * @secure
     */
    updateProblemFlow: (
      data: UpdateProblemFlow,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/ProblemFlow`,
        method: 'PUT',
        data,
        ...config
      })
  },
  ProblemStatus: {
    /**
     * Obtiene listado de los estados de un Problema sin paginado
     *
     * @tags ProblemStatus
     * @name getAllProblemStatus
     * @request GET:/api/v1/ProblemStatus
     * @secure
     */
    getAllProblemStatus: (
      onlyEnabled: string,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<UseGetAllProblemStatus[]>>({
        url: `/ProblemStatus?onlyEnabled=${onlyEnabled}`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene listado de los estados de un Problema paginado
     *
     * @tags ProblemStatus
     * @name getProblemStatus
     * @request POST:/api/v1/ProblemStatus/GeneralSearch
     * @secure
     */
    getProblemStatus: (
      data: ProblemStatusSearchRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<ProblemStatusResponse[]>>
      >({
        url: `/ProblemStatus/GeneralSearch`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene listado de los estados de un Problema por ID
     *
     * @tags ProblemStatus
     * @name getProblemStatus
     * @request GET:/api/v1/ProblemStatus/${id}
     * @secure
     */
    getProblemStatusById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ProblemStatusResponse>>({
        url: `/ProblemStatus/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene listado de los estados de cliente por ID
     *
     * @tags ProblemStatus
     * @name getProblemStatusByClientId
     * @request GET:/api/v1/ProblemStatus/${id}
     * @secure
     */
    getProblemStatusByClientId: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<ProblemStatusByClientResponse[]>
      >({
        url: `/ProblemStatus/Client/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene listado de los estados disponibles para cambiar por problemId
     *
     * @tags ProblemStatus
     * @name getProblemStatusAvailable
     * @request GET:/api/v1/ProblemStatus/${problemId}/Available
     * @secure
     */
    getProblemStatusAvailable: (
      problemId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<ProblemStatusByClientResponse[]>
      >({
        url: `/ProblemStatus/Problem/${problemId}/Available`,
        method: 'GET',
        ...config
      }),
    /**
     * Crea un nuevo estado de un Problema
     *
     * @tags ProblemStatus
     * @name createProblemStatus
     * @request POST:/api/v1/ProblemStatus
     * @secure
     */
    createProblemStatus: (
      data: CreateProblemStatusRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ProblemStatusResponse>>({
        url: `/ProblemStatus`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Actualiza un estado de un Problema
     *
     * @tags ProblemStatus
     * @name updateProblemStatus
     * @request PUT:/api/v1/ProblemStatus
     * @secure
     */
    updateProblemStatus: (
      data: EditProblemStatusRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ProblemStatusResponse>>({
        url: `/ProblemStatus`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Elimina un estado de un Problema
     *
     * @tags ProblemStatus
     * @name deleteProblemStatus
     * @request DELETE:/api/v1/ProblemStatus/${id}
     * @secure
     */
    deleteProblemStatus: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ProblemStatusResponse>>({
        url: `/ProblemStatus/${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Obtiene listado de las categorías de un Problema
     *
     * @tags ProblemStatus
     * @name getAllProblemCategories
     * @request GET:/api/v1/ProblemStatus/Categories
     * @secure
     */
    getAllProblemCategories: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<GetAllProblemCategories[]>>({
        url: `/ProblemStatus/Categories?excludeAllCategory=true`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene listado de los clientes sin flujo de estatus de Problemas
     *
     * @tags ProblemStatus
     * @name getClientsWhitOutFlow
     * @request GET:/api/v1/Clients
     * @secure
     */
    getClientsWhitOutFlow: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<GetClientsWhitOutFlow[]>>({
        url: `/Clients`,
        method: 'GET',
        ...config
      })
  },
  SolutionType: {
    /**
     * Get all solution types
     * @tags SolutionType
     * @name getAllSolutionTypePaginated
     * @request GET: api/v1/SolutionType/search
     * @secure
     */
    getAllSolutionTypePaginated: (
      data: SolutionTypeRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<SolutionTypeResponse[]>>
      >({
        url: `/SolutionType/search`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Create new solution type
     * @tags SolutionType
     * @name createSolutionType
     * @request POST: api/v1/SolutionType
     * @secure
     */
    createSolutionType: (
      data: SolutionTypeCreateRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/SolutionType`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Edit solution type
     * @tags SolutionType
     * @name editSolutionType
     * @request PUT: api/v1/SolutionType
     * @secure
     */
    editSolutionType: (
      data: SolutionTypeEditRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/SolutionType`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Delete solution type
     * @tags SolutionType
     * @name deleteSolutionType
     * @request DELETE: api/v1/SolutionType/${id}
     * @secure
     */
    deleteSolutionType: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/SolutionType/${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Get all solution types
     * @tags SolutionType
     * @name getAllSolutionType
     * @request GET: api/v1/SolutionType?onlyEnabled=true
     * @secure
     */
    getAllSolutionType: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown[]>>({
        url: `/SolutionType?onlyEnabled=true`,
        method: 'GET',
        ...config
      })
  },
  releases: {
    /**
     * Búsqueda general de releases
     *
     * @tags Releases
     * @name search
     * @request POST: /api/v1/Releases/search
     * @secure
     */
    search: (data: IReleaseSearchRequest, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<IReleaseSearchResponse>>
      >({
        url: `/Releases/search`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene un Release por su ID
     *
     * @tags Releases
     * @name getById
     * @request GET: /api/v1/Releases/{id}
     * @secure
     */
    getById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IReleaseDetail>>({
        url: `/Releases/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Crea un nuevo Release
     *
     * @tags Releases
     * @name create
     * @request POST: /api/v1/Releases
     * @secure
     */
    create: (data: ICreateRelease, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<{ releaseId: number; title: string }>
      >({
        url: `/Releases`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Actualiza un Release
     *
     * @tags Releases
     * @name update
     * @request PUT: /api/v1/Releases
     * @secure
     */
    update: (data: ICreateRelease, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<{ id: number }>>({
        url: `/Releases`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Elimina un Release
     *
     * @tags Releases
     * @name delete
     * @request DELETE: /api/v1/Releases/{id}
     * @secure
     */
    delete: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<{ id: number }>>({
        url: `/Releases/${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Actualiza el estatus de un release
     *
     * @tags Releases
     * @name UpdateReleaseStatus
     * @request POST: /api/v1/Releases/UpdateReleaseStatus
     * @secure
     */
    UpdateReleaseStatus: (
      data: IUpdateReleaseStatus,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Releases/UpdateReleaseStatus`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Asignar un release
     *
     * @tags Releases
     * @name AssignRelease
     * @request POST: /api/v1/Releases/AssignRelease
     * @secure
     */
    AssignRelease: (data: IAssignRelease, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Releases/AssignRelease`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Actualizar la prioridad de un release
     *
     * @tags Releases
     * @name UpdateReleasePriority
     * @request POST: /api/v1/Releases/UpdateReleasePriority
     * @secure
     */
    UpdateReleasePriority: (
      data: IUpdateReleasePriority,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Releases/UpdateReleasePriority`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Agrega uno o varios productos al Release
     *
     * @tags Releases
     * @name addProducts
     * @request POST: /api/v1/Releases/{id}/products
     * @secure
     */
    addProducts: (
      releaseId: number,
      data: { productIds: number[] },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Releases/${releaseId}/products`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Elimina uno o varios productos del Release
     *
     * @tags Releases
     * @name deleteProducts
     * @request DELETE: /api/v1/Releases/{id}/products
     * @secure
     */
    deleteProducts: (
      releaseId: number,
      data: { productIds: number[] },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ id: number; title: string }>
      >({
        url: `/Releases/${releaseId}/products`,
        method: 'DELETE',
        data,
        ...config
      }),
    /**
     * Obtiene las Soluciones asociadas a un Release
     *
     * @tags Releases
     * @name getRelatedSolutions
     * @request GET: /api/v1/Releases/{id}/solutions
     * @secure
     */
    getRelatedSolutions: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SearchSolutionsResponse[]>>({
        url: `/Releases/${id}/solutions`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene las solicitudes vinculadas a un release
     *
     * @tags Releases
     * @name getRelatedRequests
     * @request GET: /api/v1/Releases/{id}/RelatedTickets
     * @secure
     */
    getRelatedRequests: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ITicketProblemResponse[]>>({
        url: `/Releases/${id}/RelatedTickets`,
        method: 'GET',
        ...config
      }),
    /**
     * Establece un ticket de tipo "Solicitud" como relacionado al release
     *
     * @tags Releases
     * @name SetRelatedTicket
     * @request POST: /api/v1/Releases/SetRelatedTicket
     * @secure
     */
    SetRelatedTicket: (
      data: IRelatedTicketProblem,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Releases/SetRelatedTicket`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Elimina la relación entre un ticket y un release
     *
     * @tags Releases
     * @name RemoveRelatedTicket
     * @request DELETE: /api/v1/Releases/RemoveRelatedTicket
     * @secure
     */
    RemoveRelatedTicket: (
      data: IRelatedTicketProblem,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Releases/RemoveRelatedTicket`,
        method: 'DELETE',
        data,
        ...config
      }),
    /**
     * Obtiene las secciones de análisis de releases
     *
     * @tags Releases
     * @name getAnalysisSections
     * @request GET: /api/v1/Releases/AnalysisSections
     * @secure
     */
    getAnalysisSections: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<IAnalysisSection[]>>({
        url: `/Releases/AnalysisSections`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene las notas asociadas a un Release
     *
     * @tags Releases
     * @name getNotes
     * @request GET: /api/v1/Releases/{id}/note
     * @secure
     */
    getNotes: (
      releaseId: number,
      params: { analysisSectionId?: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IAnalysisSectionNotes[]>>({
        url: `/Releases/${releaseId}/note`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Guardar una Nota de un Release
     *
     * @tags Releases
     * @name CreateReleaseNote
     * @request POST: /api/v1/Releases/CreateReleaseNote
     * @secure
     */
    CreateReleaseNote: (
      data: ICreateProblemNote,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Releases/CreateReleaseNote`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Actualiza una Nota de un Release
     *
     * @tags Releases
     * @name UpdateNote
     * @request PUT: /api/v1/Releases/UpdateNote
     * @secure
     */
    UpdateNote: (data: IUpdateProblemNote, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          noteId?: number;
        }>
      >({
        url: `/Releases/UpdateNote`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Eliminar una Nota de un release
     *
     * @tags Releases
     * @name DeleteReleaseNote
     * @request DELETE: /api/v1/Releases/DeleteReleaseNote
     * @secure
     */
    DeleteReleaseNote: (
      params: { noteId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Releases/DeleteReleaseNote`,
        method: 'DELETE',
        params,
        ...config
      }),
    /**
     * Crea nuevas notas en un lanzamiento
     *
     * @tags Releases
     * @name CreateReleaseNotes
     * @request POST: /api/v1/Releases/123/notes
     * @secure
     */
    CreateReleaseNotes: (
      releaseId: number,
      data: ICreateReleaseNote[],
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{
          id?: number;
        }>
      >({
        url: `/Releases/${releaseId}/notes`,
        method: 'POST',
        data,
        ...config
      })
  },
  ReleaseStatus: {
    /**
     * Obtiene listado de los estados de un Release paginado
     *
     * @tags ReleaseStatus
     * @name generalSearchReleaseStatus
     * @request POST: /api/v1/ReleaseStatus
     * @secure
     */
    generalSearchReleaseStatus: (
      data: IParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<ReleaseStatusResponse[]>>
      >({
        url: `/ReleaseStatus/GeneralSearch`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene listado de los estados de un Release por ID de cliente
     *
     * @tags ReleaseStatus
     * @name getAllReleaseStatusByClientId
     * @request GET: /api/v1/ReleaseStatus/Client/${clientId}
     * @secure
     */
    getAllReleaseStatusByClientId: (
      clientId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<GetAllReleaseStatusResponse[]>
      >({
        url: `/ReleaseStatus/Client/${clientId}`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene listado de los estados de cliente por ID
     *
     * @tags ReleaseStatus
     * @name getReleaseStatusByClientId
     * @request GET: /api/v1/ReleaseStatus/Client/${id}
     * @secure
     */
    getReleaseStatusByClientId: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<ProblemStatusByClientResponse[]>
      >({
        url: `/ReleaseStatus/Client/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene listado de los estados disponibles para cambiar por releaseId
     *
     * @tags ReleaseStatus
     * @name getReleaseStatusAvailable
     * @request GET: /api/v1/ReleaseStatus/Release/${releaseId}/Available
     * @secure
     */
    getReleaseStatusAvailable: (
      releaseId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<ProblemStatusByClientResponse[]>
      >({
        url: `/ReleaseStatus/Release/${releaseId}/Available`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene listado de las categorías de un Release
     *
     * @tags ReleaseStatus
     * @name getAllReleaseCategories
     * @request GET: /api/v1/ReleaseStatus/Categories?excludeAllCategory=true
     * @secure
     */
    getAllReleaseCategories: (
      excludeAllCategory = 'true',
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ReleaseStatusCategories[]>>({
        url: `/ReleaseStatus/Categories?excludeAllCategory=${excludeAllCategory}`,
        method: 'GET',
        ...config
      }),
    /**
     * Crea un nuevo estado de un Release
     *
     * @tags ReleaseStatus
     * @name createReleaseStatus
     * @request POST:/api/v1/ReleaseStatus
     * @secure
     */
    createReleaseStatus: (
      data: CreateReleaseStatusRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ReleaseStatusResponse>>({
        url: `/ReleaseStatus`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Actualiza un estado de un Release
     *
     * @tags ReleaseStatus
     * @name updateReleaseStatus
     * @request PUT:/api/v1/ReleaseStatus
     * @secure
     */
    updateReleaseStatus: (
      data: EditReleaseStatusRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ReleaseStatusResponse>>({
        url: `/ReleaseStatus`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Elimina un estado de un Release
     *
     * @tags ReleaseStatus
     * @name deleteReleaseStatus
     * @request DELETE:/api/v1/ReleaseStatus/${id}
     * @secure
     */
    deleteReleaseStatus: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ReleaseStatusResponse>>({
        url: `/ReleaseStatus/${id}`,
        method: 'DELETE',
        ...config
      })
  },
  ReleaseFlow: {
    /**
     * Obtiene listado de los estados de un Problema
     *
     * @tags ReleaseFlow
     * @name generalSearchProblemStatusFlow
     * @request POST:/api/v1/ReleaseFlow/search
     * @secure
     */
    generalSearchProblemStatusFlow: (
      data: IParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<ReleaseFlowSearch[]>>
      >({
        url: `/ReleaseFlow/search`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene listado de los estados de un Problema por ID
     *
     * @tags ReleaseFlow
     * @name generalSearchReleaseStatusFlowById
     * @request GET:/api/v1/ReleaseFlow/${id}
     * @secure
     */
    generalSearchReleaseStatusFlowById: (
      id: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ReleaseFlowSearchIDResponse>>(
        {
          url: `/ReleaseFlow/${id}`,
          method: 'GET',
          ...config
        }
      ),
    /**
     * Crea un nuevo flujo de estatus de Problemas
     *
     * @tags ReleaseFlow
     * @name createNewStatus
     * @request POST: api/v1/ReleaseFlow
     * @secure
     */
    createNewStatus: (
      data: CreateReleaseFlowRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ReleaseFlowSearchIDResponse>>(
        {
          url: `/ReleaseFlow`,
          method: 'POST',
          data,
          ...config
        }
      ),
    /**
     * Obtener el diagrama de Gantt de un flujo de estatus de Releases|
     *
     * @tags ReleaseFlow
     * @name getGanttDiagram
     * @request GET: /api/v1/ReleaseFlow/1/gantt-diagram
     * @secure
     */
    getGanttDiagram: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ProblemFlowSearchIDResponse>>(
        {
          url: `/ReleaseFlow/${id}/gantt-diagram`,
          method: 'GET',
          ...config
        }
      ),
    /**
     * Elimina un flujo de estatus de Problemas
     *
     * @tags ReleaseFlow
     * @name deleteReleaseFlow
     * @request DELETE: api/v1/ReleaseFlow/{id}
     * @secure
     */
    deleteReleaseFlow: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/ReleaseFlow/${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Actualiza un flujo de estatus de Problemas
     *
     * @tags ReleaseFlow
     * @name updateReleaseFlow
     * @request PUT: api/v1/ReleaseFlow
     * @secure
     */
    updateReleaseFlow: (
      data: UpdateReleaseFlow,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/ReleaseFlow`,
        method: 'PUT',
        data,
        ...config
      })
  }
};
